/**
 * Revision History
 * 
 * 12/28/2021 ptkim       [save-sql] storageVersion must be increased whenever you change any TableSettings to update browser cache.
 * 02/22/2022 dario       for better control tableVersion can be set inside concrete TableSettings. If it exists it overrides storageVersion
 *                        so not every table in application is affected by version upgrade.
*/
import React from "react";
import {XAlignType, XCellType} from "../framework.core/XExportColumn";

export type HeaderFilterType = 'DateTime' | 'Date' | 'Number' | 'TextBegin' | 'TextSelect' | 'Bool' | 'None' | 'Special';

// Be cautious when modifying storageVersion as it will reset all saved Column Options across different machines
// You should modify the tableVersion defined for each Table instead
// Changing tableVersion will only reset Column Options for the specific Table that was modified
export const storageVersion = 1081; 

export interface TableOrderBy {
  orderBy: string,
  orderByAscending: boolean,
};

export interface TableColumn {
  id?: string,
  accessor: string,
  Cell?: any,
  Header?: any,
  visible: boolean,
  resizable?: boolean,
  sortable?: boolean,
  filterable?: boolean,
  //<pt211110: Add XCellType>
  /**
   * @deprecated
  */
  //filterType?: 'text' | 'number' | 'float' | 'date' | 'bool'| 'location' | undefined | XCellType,
  filterType?: string | undefined | XCellType,
  filterTypeV2?: XCellType | undefined,
  minWidth?: number | undefined,
  maxWidth?: number | undefined,
  width?: number | undefined,
  initialWidthPercentage?: number|undefined,
  defaultColumnFocus?: boolean,
  canBeHidden?: boolean,
  hiddenInColumnSetup?: boolean,
  hasCustomTooltip?: boolean,
  tooltip?: string|undefined,
  align?: XAlignType,   //<pt211110>
  noNullOrEmptySearch?: boolean,
  formatString?: string,
  headerFilterType?: HeaderFilterType,
  columnIndicator?: number,
};

export interface TableSettings {
  overflowX?: string|undefined,
  overflowY?: string|undefined,
  height?: string|undefined,
  useStorage?: boolean,
  flexResizer?: boolean,
  smallHeaders?: boolean,
  noTooltip?: boolean,
  headersColor?: 'gray'|'yellow'|'white'|'teal'|'lightgray'|'purple'|undefined,
  columns: Array<TableColumn>,
  alternateRows?: boolean,
  blackMode?: boolean,
  tableVersion?: number|undefined,
  useHighlightCellOnMouseover?:boolean,
  useHeaderFilters?:boolean,
  useHeaderFilterParams?: { setData: (data: any[]) => void, useHeaderFilter: boolean, oriListData: any[], disableBtnNameWhenFilterModalOpen: string };
  useSetClickedCellWhenClick?:boolean,
  noHeaders?: boolean,
  headerFilterType?: HeaderFilterType
  headersFontColor?: string|undefined,
  /**
   * Indicate if columns are customized or not (pt250426)
   */
  isCustomized?: boolean,
  [key: string]: any,
};

export interface ApplicableTableSettings extends TableSettings {
  data: Array<any>
  tableName: string,
};

export interface TableRowRenderer {
  accessor: 'string',
  Cell?: React.ReactNode,
  Header?: React.ReactNode,
}

export function createColumnFocusListHierarchy(settings:TableSettings) {
  if (!settings || !settings.columns) {
    return [];
  }

  const result:Array<string> = [];
  let anchorFound = false;

  settings.columns.forEach((c:TableColumn) => {
    if (c.defaultColumnFocus) {
      result.push(c.accessor);
      anchorFound = true;

      return;
    }

    if (anchorFound) {
      result.push(c.accessor);
    }
  });

  return result;
}

export const invoiceLineItems:TableSettings = {
  tableVersion: 10048,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '320px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',

  columns: [
    { Header: '#', accessor: 'indicator',  resizable: true,  sortable: false,  visible: true, initialWidthPercentage: 2.63, },
    { Header: 'Type', accessor: 'chgTypeDesc', resizable: true, sortable: false,visible: true, initialWidthPercentage: 9.65, },
    { Header: 'Billing Code', accessor: 'billingCodeView', resizable: true, sortable: false, visible: true, initialWidthPercentage: 10.09, },
    { Header: 'Item Description', accessor: 'description', resizable: true, sortable: false, visible: true, initialWidthPercentage: 10.53, },
    { Header: 'CUR', accessor: 'cur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 5.26,},
    { Header: 'Rate', accessor: 'rate', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.02, },
    { Header: 'Qty', accessor: 'qty', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.02, },
    { Header: 'Amount', accessor: 'curAmtDesc', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.89, },
    { Header: 'USD Amount', accessor: 'amtDesc', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.89, },
    { Header: 'Agent', accessor: 'agentName', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.89, },
    { Header: 'CUR', accessor: 'agtCur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 5.26, },
    { Header: 'Agent Amount', accessor: 'agtAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.02, },
    { Header: 'CrDb #', accessor: 'crDbNo', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.89, },
    { Header: 'PS', accessor: 'ps', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.19, },
    // { Header: 'Del', accessor: 'commands', resizable: false, sortable: false, visible: true, initialWidthPercentage: 2.02, },
    { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, initialWidthPercentage: 1.77, },
  ],
};
export const gInvoiceLineItems:TableSettings = {
  tableVersion: 1010,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '320px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',

  columns: [
    { Header: '#', accessor: 'indicator', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.63, },
    { Header: 'Billing Code *', accessor: 'billingCodeView', resizable: true, sortable: false, visible: true, initialWidthPercentage: 22.5, },
    { Header: 'Item Description', accessor: 'description', resizable: true, sortable: false, visible: true, initialWidthPercentage: 30.28, },
    { Header: 'CUR', accessor: 'cur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 5.26, },
    { Header: 'Rate', accessor: 'rate', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.77, },
    { Header: 'Qty', accessor: 'qty', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.77, },
    { Header: 'Amount', accessor: 'curAmtDesc', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.77, },
    { Header: 'USD Amount', accessor: 'lAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.77, },
    { Header: 'VAT', accessor: 'useVat', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.5, },
    { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, initialWidthPercentage: 1.75, },
  ],
};

export const billLineItems:TableSettings = {
  tableVersion: 1010,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '320px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',

  columns: [
    { Header: '#', accessor: 'indicator',  resizable: true,  sortable: false,  visible: true, initialWidthPercentage: 2.63, },
    { Header: 'Type', accessor: 'chgTypeDesc', resizable: true, sortable: false,visible: true, initialWidthPercentage: 9.65, },
    { Header: 'Billing Code', accessor: 'billingCodeView', resizable: true, sortable: false, visible: true, initialWidthPercentage: 10.09, },
    { Header: 'Item Description', accessor: 'description', resizable: true, sortable: false, visible: true, initialWidthPercentage: 10.53, },
    { Header: 'CUR', accessor: 'cur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 5.26,},
    { Header: 'Rate', accessor: 'rate', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.02, },
    { Header: 'Qty', accessor: 'qty', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.02, },
    { Header: 'Amount', accessor: 'amount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.89, },
    { Header: 'USD Amount', accessor: 'lAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.89, },
    //{ Header: 'VAT', accessor: 'useVat', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.19, },
    { Header: 'Agent', accessor: 'agentName', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.89, },
    { Header: 'CUR', accessor: 'agtCur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 5.26, },
    { Header: 'Agent Amount', accessor: 'agtAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.02, },
    { Header: 'CrDb #', accessor: 'crDbNo', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.89, },
    { Header: 'PS', accessor: 'ps', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.19, },
    // { Header: 'Del', accessor: 'commands', resizable: false, sortable: false, visible: true, initialWidthPercentage: 2.02, },
    { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, initialWidthPercentage: 1.77, },
  ],
};

export const invoiceLineItemsWithVat:TableSettings = {
  tableVersion: 10047,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '320px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',

  columns: [
    { Header: '#', accessor: 'indicator', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.1, },
    { Header: 'Type', accessor: 'chgTypeDesc', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.9, },
    { Header: 'Billing Code', accessor: 'billingCodeView', resizable: true, sortable: false, visible: true, initialWidthPercentage: 11, },
    { Header: 'Item Description', accessor: 'description', resizable: true, sortable: false, visible: true, initialWidthPercentage: 11, },
    { Header: 'CUR', accessor: 'cur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 4, },
    { Header: 'Rate', accessor: 'rate', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.28, },
    { Header: 'Qty', accessor: 'qty', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.28, },
    { Header: 'Amount', accessor: 'curAmtDesc', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.28, },
    { Header: 'USD Amount', accessor: 'amtDesc', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.28, },
    { Header: 'Agent', accessor: 'agentName', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.88, },
    { Header: 'VAT', accessor: 'useVat', resizable: true, sortable: false, visible: true, initialWidthPercentage: 3, },
    { Header: 'CUR', accessor: 'agtCur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 4, },
    { Header: 'Agent Amount', accessor: 'agtAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.28, },
    { Header: 'CrDb #', accessor: 'crDbNo', resizable: true, sortable: false, visible: true, initialWidthPercentage: 9, },
    { Header: 'PS', accessor: 'ps', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.1, },
    // { Header: 'Del', accessor: 'commands', resizable: false, sortable: false, visible: true, initialWidthPercentage: 2.02, },
    { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, initialWidthPercentage: 1.62,},
  ]
};

export const gInvoiceLineItemsWithCreditAmount:TableSettings = {
  tableVersion: 1009,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '320px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',

  columns: [
    { Header: '#', accessor: 'indicator', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.63, },
    { Header: 'Billing Code *', accessor: 'billingCodeView', resizable: true, sortable: false, visible: true, initialWidthPercentage: 19.01, },     
    { Header: 'Item Description', accessor: 'description', resizable: true, sortable: false, visible: true, initialWidthPercentage: 25, },   
    { Header: 'CUR', accessor: 'cur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 5.26, },
    { Header: 'Rate', accessor: 'rate', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.77, },
    { Header: 'Qty', accessor: 'qty', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.77, },
    { Header: 'Amount', accessor: 'curAmtDesc', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.77, },
    { Header: 'USD Amount', accessor: 'lAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.77, },
    { Header: 'VAT', accessor: 'useVat', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.5, },   
    { Header: 'Credit Amount', accessor: 'agtAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.77, },  
    { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, initialWidthPercentage: 1.75, },
  ],
};

export const gBillLineItemsWithCreditAmount: TableSettings = {
  tableVersion: 1011,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '320px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',

  columns: [
    { Header: '#', accessor: 'indicator', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.63,},
    { Header: 'Billing Code *', accessor: 'billingCodeView', resizable: true, sortable: false, visible: true, initialWidthPercentage: 19.01,},
    { Header: 'Item Description', accessor: 'description', resizable: true, sortable: false, visible: true, initialWidthPercentage: 25,},
    { Header: 'CUR', accessor: 'cur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 5.26,},
    { Header: 'Rate', accessor: 'rate', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.77,},
    { Header: 'Qty', accessor: 'qty', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.77,},
    { Header: 'Amount', accessor: 'amount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.77,},
    { Header: 'USD Amount', accessor: 'lAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.77, },
    /*{ Header: 'Del', accessor: 'commands', resizable: false, sortable: false, visible: true, initialWidthPercentage: 3.07,},*/
    { Header: 'VAT', accessor: 'useVat', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.5,},
    { Header: 'Credit Amount', accessor: 'agtAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.77, },  
    { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, initialWidthPercentage: 1.75,},
  ],
};

export const gBillLineItems: TableSettings = {
  tableVersion: 1009,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '320px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',

  columns: [
    { Header: '#', accessor: 'indicator', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.63,},
    { Header: 'Billing Code *', accessor: 'billingCodeView', resizable: true, sortable: false, visible: true, initialWidthPercentage: 22.5,},
    { Header: 'Item Description', accessor: 'description', resizable: true, sortable: false, visible: true, initialWidthPercentage: 30.28,},
    { Header: 'CUR', accessor: 'cur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 5.26,},
    { Header: 'Rate', accessor: 'rate', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.77,},
    { Header: 'Qty', accessor: 'qty', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.77,},
    { Header: 'Amount', accessor: 'amount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.77,},
    { Header: 'USD Amount', accessor: 'lAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.77, },
    { Header: 'VAT', accessor: 'useVat', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.5, },
    /*{ Header: 'Del', accessor: 'commands', resizable: false, sortable: false, visible: true, initialWidthPercentage: 3.07,},*/
    { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, initialWidthPercentage: 1.75,},
  ],
};

export const billLineItemsWithVat:TableSettings = {
  tableVersion: 1007,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '320px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',

  columns: [
    { Header: '#', accessor: 'indicator', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.1, },
    { Header: 'Type', accessor: 'chgTypeDesc', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.90, },
    { Header: 'Billing Code', accessor: 'billingCodeView', resizable: true, sortable: false, visible: true, initialWidthPercentage: 11, },
    { Header: 'Item Description', accessor: 'description', resizable: true, sortable: false, visible: true, initialWidthPercentage: 11, },
    { Header: 'CUR', accessor: 'cur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 4, },
    { Header: 'Rate', accessor: 'rate', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.28, },
    { Header: 'Qty', accessor: 'qty', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.28, },
    { Header: 'Amount', accessor: 'amount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.28, },
    { Header: 'USD Amount', accessor: 'lAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.28, },
    { Header: 'Agent', accessor: 'agentName', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.88, },
    { Header: 'VAT', accessor: 'useVat', resizable: true, sortable: false, visible: true, initialWidthPercentage: 3, },
    { Header: 'CUR', accessor: 'agtCur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 4, },
    { Header: 'Agent Amount', accessor: 'agtAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.28, },
    { Header: 'CrDb #', accessor: 'crDbNo', resizable: true, sortable: false, visible: true, initialWidthPercentage: 9, },
    { Header: 'PS', accessor: 'ps', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.1, },
    // { Header: 'Del', accessor: 'commands', resizable: false, sortable: false, visible: true, initialWidthPercentage: 2.02, },
    { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, initialWidthPercentage: 1.62,},
  ]
};

export const invoicePreviewLineItems:TableSettings = {
  tableVersion: 1002,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '100%',
  useStorage: true,
  flexResizer: true,
  headersColor: 'gray',

  
  columns: [
    { Header: 'Billing Code', accessor: 'billingCode', resizable: true, sortable: false, visible: true, initialWidthPercentage: 13.11, },
    { Header: 'Item Description', accessor: 'description', resizable: true, sortable: false, visible: true, initialWidthPercentage: 18.85, },
    { Header: 'CUR', accessor: 'cur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 4.55, },
    { Header: 'Rate', accessor: 'rate', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.82, },
    { Header: 'Qty', accessor: 'qty', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.82, },
    { Header: 'Amount', accessor: 'curAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.82, },
    { Header: 'USD Amount', accessor: 'amount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.82, },
    { Header: 'Agent', accessor: 'agentName', resizable: true, sortable: false, visible: true, initialWidthPercentage: 14.00, },
    { Header: 'CUR', accessor: 'agtCur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 4.55, },
    { Header: 'Agent Amount', accessor: 'agtAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.27, },
    { Header: 'CrDb #', accessor: 'crDbNo', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.00, },
    { Header: 'PS', accessor: 'ps', resizable: true, sortable: false, visible: true, initialWidthPercentage: 1.89, },
    { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, initialWidthPercentage: 1.50, },
  ],
};

export const billPreviewLineItems:TableSettings = {
  tableVersion: 1002,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '100%',
  useStorage: true,
  flexResizer: true,
  headersColor: 'gray',

  
  columns: [
    { Header: 'Billing Code', accessor: 'billingCode', resizable: true, sortable: false, visible: true, initialWidthPercentage: 13.11, },
    { Header: 'Item Description', accessor: 'description', resizable: true, sortable: false, visible: true, initialWidthPercentage: 18.85, },
    { Header: 'CUR', accessor: 'cur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 4.55, },
    { Header: 'Rate', accessor: 'rate', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.82, },
    { Header: 'Qty', accessor: 'qty', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.82, },
    { Header: 'Amount', accessor: 'amount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.82, },
    { Header: 'USD Amount', accessor: 'lAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.82, },
    { Header: 'Agent', accessor: 'agentName', resizable: true, sortable: false, visible: true, initialWidthPercentage: 14.00, },
    { Header: 'CUR', accessor: 'agtCur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 4.55, },
    { Header: 'Agent Amount', accessor: 'agtAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.27, },
    { Header: 'CrDb #', accessor: 'crDbNo', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.00, },
    { Header: 'PS', accessor: 'ps', resizable: true, sortable: false, visible: true, initialWidthPercentage: 1.89, },
    { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, initialWidthPercentage: 1.50, },
  ],
};

//<pt221014: Donot use anymore
// export const billOrInvoiceCreditLineItems:TableSettings = {
//   tableVersion: 1001,
//   overflowX: 'hidden',
//   overflowY: 'scroll',
//   
//   height: '320px',
//   useStorage: true,
//   flexResizer: true,
//   headersColor: 'gray',
// 
//   columns: [
//     { Header: 'No', accessor: 'indicator', resizable: true, sortable: false, visible: true, initialWidthPercentage: 1.89, },
//     { Header: 'Type', accessor: 'chgTypeDesc', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.48, },
//     { Header: 'Billing Code', accessor: 'billingCode', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.11, },
//     { Header: 'Item Description', accessor: 'description', resizable: true, sortable: false, visible: true, initialWidthPercentage: 13.85, },
//     { Header: 'CUR', accessor: 'cur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 4.55, },
//     { Header: 'Rate', accessor: 'rate', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.82, },
//     { Header: 'Qty',  accessor: 'qty', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.82, },
//     { Header: 'Amount', accessor: 'curAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.82, },
//     { Header: 'USD Amount', accessor: 'amount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.82, },
//     { Header: 'Credit Amount', accessor: 'agtAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 11.63, },
//     // { Header: 'CUR', accessor: 'agtCur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 4.55, },
//     // { Header: 'Agent Amount', accessor: 'agtAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.27, },
//     // { Header: 'CrDb No', accessor: 'crDbNo', resizable: true, sortable: false, visible: true, initialWidthPercentage: 9.00, },
//     // { Header: 'PS', accessor: 'ps', resizable: true, sortable: false, visible: true, initialWidthPercentage: 1.89,},
//     { Header: 'Agent', accessor: 'agentName', resizable:true, sortable: false, visible: true, initialWidthPercentage: 9.00},
//     // { Header: 'Del', accessor: 'commands', resizable: false, sortable: false, visible: true, initialWidthPercentage: 2.00, },
//     { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, initialWidthPercentage: 1.50, },
//   ]
// };

// export const billOrInvoiceCreditLineItemsWithVat:TableSettings = {
//   tableVersion: 1001,
//   overflowX: 'hidden',
//   overflowY: 'scroll',
//   
//   height: '320px',
//   useStorage: true,
//   flexResizer: true,
//   headersColor: 'gray',
// 
//   columns: [
//     { Header: 'No', accessor: 'indicator', resizable: true, sortable: false, visible: true, initialWidthPercentage: 1.89, },
//     { Header: 'Type', accessor: 'chgTypeDesc', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.48, },
//     { Header: 'Billing Code', accessor: 'billingCode', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.11, },
//     { Header: 'Item Description', accessor: 'description', resizable: true, sortable: false, visible: true, initialWidthPercentage: 12.20, },
//     { Header: 'CUR', accessor: 'cur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 4.55, },
//     { Header: 'Rate', accessor: 'rate', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.82, },
//     { Header: 'Qty', accessor: 'qty', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.82, },
//     { Header: 'Amount', accessor: 'curAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.82, },
//     { Header: 'USD Amount', accessor: 'amount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.82, },
//     { Header: 'Credit Amount', accessor: 'agtAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 10.63, },
//     { Header: 'VAT', accessor: 'useVat', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.65, },
//     // { Header: 'CUR', accessor: 'agtCur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 4.55, },
//     // { Header: 'Agent Amount', accessor: 'agtAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.27, },
//     // { Header: 'CrDb No', accessor: 'crDbNo', resizable: true, sortable: false, visible: true, initialWidthPercentage: 9.00, },
//     // { Header: 'PS', accessor: 'ps', resizable: true, sortable: false, visible: true, initialWidthPercentage: 1.89, },
//     { Header: 'Agent', accessor: 'agentName', resizable:true, sortable: false, visible: true, initialWidthPercentage: 9.00},
//     // { Header: 'Del', accessor: 'commands', resizable: false, sortable: false, visible: true, initialWidthPercentage: 2.00, },
//     { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, initialWidthPercentage: 1.50, },
//   ]
// };
//pt221014>

export const companyContactLineItems:TableSettings = {
  tableVersion: 1008,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '145px',
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.09,
    },
    {
      Header: 'Name',
      accessor: 'contact',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 12.37,
    },
    {
      Header: 'Department',
      accessor: 'dept',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.25,
    },
    {
      Header: 'Phone #',
      accessor: 'phone',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.28,
    },
    {
      Header: 'Fax #',
      accessor: 'fax',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.28,
    },
    {
      Header: 'Email Address',
      accessor: 'email',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 21.62,
    },
    {
      Header: 'Event',
      accessor: 'sendEvent',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.19,
    },
    {
      Header: 'Remark',
      accessor: 'remark',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 20.62,
    },
    {
      Header: 'Primary',
      accessor: 'isPrimary',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 5.25,
    },
    {
      Header: '',
      accessor: 'scroll',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.05,
    },
  ]
};

export const companyBrokerLineItems:TableSettings = {
  tableVersion: 1006,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '145px',
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.09,
    },
    {
      Header: 'Company Name',
      accessor: 'brokerName',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 12.87,
    },
    {
      Header: 'Dest',
      accessor: 'destination',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.75,
    },
    {
      Header: 'Address',
      accessor: 'brokerAddress',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.28,
    },
    {
      Header: 'Contact',
      accessor: 'contact',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 17.53,
    },
    {
      Header: 'Phone #',
      accessor: 'phone',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.28,
    },
    {
      Header: 'Fax #',
      accessor: 'fax',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.28,
    },
    {
      Header: 'Email Address',
      accessor: 'email',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 20.62,
    },
    {
      Header: 'Primary',
      accessor: 'isPrimary',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 5.25,
    },
    {
      Header: '',
      accessor: 'scroll',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.05,
    },
  ]
};

export const companyDeliveryLineItems: TableSettings = {
    tableVersion: 1005,
    overflowX: 'hidden',
    overflowY: 'scroll',
    height: '145px',
    useStorage: true,
    flexResizer: true,
  
    headersColor: 'white',
    columns: [
        { Header: '', accessor: 'indicator', resizable: true, sortable: false, visible: true, initialWidthPercentage: 3.0, },
        { Header: 'Company Name', accessor: 'fName', resizable: true, sortable: false, visible: true, initialWidthPercentage: 12.0, },
        { Header: 'Contact', accessor: 'contact', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.5, },
        { Header: 'Address', accessor: 'address', resizable: true, sortable: false, visible: true, initialWidthPercentage: 13.0, },
        { Header: 'City', accessor: 'city', resizable: true, sortable: false, visible: true, initialWidthPercentage: 10.0, },
        { Header: 'State', accessor: 'state', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.0, },
        { Header: 'Zip Code', accessor: 'zipCode', resizable: true, sortable: false, visible: true, initialWidthPercentage: 5.2, },
        { Header: 'Country', accessor: 'country', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.0, },
        { Header: 'Phone #', accessor: 'phone', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.25, },
        { Header: 'Fax #', accessor: 'fax', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.25, },
        { Header: 'Email Address', accessor: 'email', resizable: true, sortable: false, visible: true, initialWidthPercentage: 12.5, },
        { Header: 'Primary', accessor: 'isPrimary', resizable: true, sortable: false, visible: true, initialWidthPercentage: 5.25, },
        { Header: '', accessor: 'scroll', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.05, },
    ]
};

export const companyPickupLineItems:TableSettings = {
  tableVersion: 1005,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '145px',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.09,
    },
    {
      Header: 'Company Name',
      accessor: 'company',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 12.37,
    },
    {
      Header: 'Contact',
      accessor: 'contact',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.25,
    },
    {
      Header: 'Address',
      accessor: 'address',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 13.40,
    },
    {
      Header: 'City',
      accessor: 'city',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.19,
    },
    {
      Header: 'State',
      accessor: 'state',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.19,
    },
    {
      Header: 'Zip Code',
      accessor: 'zipCode',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 5.15,
    },
    {
      Header: 'Country',
      accessor: 'country',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.19,
    },
    {
      Header: 'Phone #',
      accessor: 'phoneNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.25,
    },
    {
      Header: 'Fax #',
      accessor: 'faxNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.25,
    },
    {
      Header: 'Email Address',
      accessor: 'emailAddress',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 12.37,
    },
    {
      Header: 'Primary',
      accessor: 'primary',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.25,
    },
    {
      Header: '',
      accessor: 'scroll',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.05,
    },
  ]
};

export const companyAutoBillingLineItems:TableSettings = {
  tableVersion: 1021,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '145px',
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.09,
    },
    {
      Header: 'Billing Code',
      accessor: 'billingCodeView',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 18.87,
    },
    {
      Header: 'OP Type',
      accessor: 'opType',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.50,
    },
    {
      Header: 'Item Description',
      accessor: 'itemDescription',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 21.15,
    },
    {
      Header: 'CUR',
      accessor: 'currency',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 5.15,
    },
    {
      Header: 'Rate',
      accessor: 'invRate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 12.56,
    },
    {
      Header: 'Qty',
      accessor: 'invQty',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 11.31,
    },
    {
      Header: 'Amount',
      accessor: 'invAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 11.31,
    },
    {
      Header: 'Group',
      accessor: 'comGroup',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8,
    },
    {
      Header: '',
      accessor: 'scroll',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.06,
    },
  ]
};

export const companyMiscInfoLineItems:TableSettings = {
  tableVersion: 1013,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '115px',
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.69,
    },
    {
      Header: 'Use',
      accessor: 'check',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 20.51,
    },
    {
      Header: 'Event',
      accessor: 'description',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 66.66,
    },
    {
      Header: '',
      accessor: 'scroll',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 5.12,
    },
  ]
};

/**
 * importer: 
 *  oceanimport/mbl/implementation/MblContainerInfo.tsx
 * pt211203: Apply OmContainer props
 */
export const mblContainerDetails:TableSettings = {
  tableVersion: 1010,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '130px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.63,
    },
    {
      Header: 'Container #',
      accessor: 'containerNo',  //'containerNumber',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.77,
    },
    {
      Header: 'Type',
      accessor: 'conType', //'type',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.89,
    },
    {
      Header: 'Seal #',
      accessor: 'sealNo', //'sealNumber',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.89,
    },
    {
      Header: 'Pkg(s)',
      accessor: 'pkgs', //'numberOfPackages',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.89,
    },
    // {
    //   Header: 'Unit',
    //   accessor: 'pUnit', //'unit',
    //   resizable: true,
    //   sortable: false,
    //   visible: true,
    //   initialWidthPercentage: 6.38,
    // },
    {
      Header: 'Weight (KGS)',
      accessor: 'kgs', //'weightKgs',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.89,
    },
    {
      Header: 'Weight (LBS)',
      accessor: 'lbs', //'weightLbs',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.89,
    },
    {
      Header: 'Pickup #',
      accessor: 'pickupNo', //'pickupNumber',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 11.40,
    },
    {
      Header: 'Pickup Location',
      accessor: 'pickupLoc', //'pickupLocation',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 13.16,
    },
    {
      Header: 'Last Free Date',
      accessor: 'lastFreeDay', //'lastFreeDate',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'G. O. Date',
      accessor: 'goDate',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Empty Return Date',
      accessor: 'emptyReturn', //'emptyReturnDate',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.77,
    },
    // {
    //   Header: 'Del',
    //   accessor: 'commands',
    //   resizable: false,
    //   sortable: false,
    //   visible: true,
    //   initialWidthPercentage: 2.43,
    // },
    {
      Header: '',
      accessor: 'scroll',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 1.78,
    },
  ]
};

/**
 * Importer:
 *  oceanImport/mbl/implementation/MblMilestone.tsx
 * pt211227: Apply OmContainer props
 */
export const mblSelectContainer:TableSettings = {
  tableVersion: 10046,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '150px',
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  columns: [
    {
      Header: '',
      accessor: 'select',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 17.39,
    },
    {
      Header: 'Container #',
      accessor: 'cntrNo', //'containerNumber',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 43.48,
    },
    {
      Header: 'Type',
      accessor: 'cntrType',   //'type',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 30.43,
    },
    {
      Header: '',
      accessor: 'scroll',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.70,
    },
  ]
};

export const mblStatus:TableSettings = {
  tableVersion: 10042,
  overflowX: 'hidden',
  overflowY: 'hidden',
  
  useStorage: true,
  flexResizer: true,

  alternateRows: false,
  headersColor: 'white',
  columns: [
    {
      Header: 'Select',
      //[save-sqlinfo]accessor: 'select',
      accessor: 'check',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 5.88,
    },
    {
      Header: 'EVENT',
      //[save-sqlinfo]accessor: 'statusName',
      accessor: 'name',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 27.94,
    },
    {
      Header: 'DATE',
      accessor: 'date',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 11.76,
    },
    {
      Header: 'TIME',
      accessor: 'time',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.35,
    },
    {
      Header: 'LOCATION (UN CODE)',
      //[save-sqlinfo]accessor: 'location',
      accessor: 'locName',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 32.35,
      minWidth: 100,
      maxWidth: 600,
    },
    {
      Header: 'Last Event Time',
      //[save-sqlinfo]accessor: 'lastEventTime',
      accessor: 'lastDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 14.72,
    },
  ]
};

export const hblBlDetailsNoOfPackages:TableSettings = {
  tableVersion: 1012,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  headersColor: 'white',
  useStorage: true,
  flexResizer: true,

  columns: [
    {
      Header: 'Container #',
      accessor: 'actualContainerNo',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 29.36,
    },
    {
      Header: 'Type',
      accessor: 'actualContainerType',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 19,
    },
    {
      Header: 'Seal #',
      accessor: 'sealNo',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 19,
    },
    {
      Header: 'Pkgs',
      accessor: 'pkgs',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 15,
    },
    {
      Header: '',
      accessor: 'agentShow',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8,
    },
    {
      Header: '',
      accessor: 'scroll',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 5.64,
    },
  ]
};

export const crdbLineItems:TableSettings = {
  tableVersion: 1014,
  headersColor: 'white',

  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '320px',
  useStorage: true,
  flexResizer: true,
  columns: [
    { Header: '#', accessor: 'indicator', resizable: false, sortable: false, visible: true, initialWidthPercentage: 2, },
    { Header: 'M/H', accessor: 'mh', resizable: false, sortable: false, visible: true, initialWidthPercentage: 3, },
    { Header: 'B/L #', accessor: 'blNo', resizable: false, sortable: false, visible: true, initialWidthPercentage: 7, },
    { Header: 'Type', accessor: 'chgTypeDesc', resizable: false, sortable: false, visible: true, initialWidthPercentage: 7.7, },
    { Header: 'Billing Code', accessor: 'crDbCodeView', resizable: false, sortable: false, visible: true, initialWidthPercentage: 9, },
    { Header: 'Item Description', accessor: 'description', resizable: false, sortable: false, visible: true, initialWidthPercentage: 12, },
    { Header: 'Rate', accessor: 'rate', resizable: false, sortable: false, visible: true, initialWidthPercentage: 7.7, },
    { Header: 'Qty', accessor: 'qty', resizable: false, sortable: false, visible: true, initialWidthPercentage: 7, },
    { Header: 'Sales', accessor: 'originRevenue', resizable: false, sortable: false, visible: true, initialWidthPercentage: 6.5, },
    { Header: 'Cost', accessor: 'originCost', resizable: false, sortable: false, visible: true, initialWidthPercentage: 6.5, },
    { Header: 'Sales', accessor: 'destRevenue', resizable: false, sortable: false, visible: true, initialWidthPercentage: 6.5, },
    { Header: 'Cost', accessor: 'destCost', resizable: false, sortable: false, visible: true, initialWidthPercentage: 6.5, },
    { Header: 'Debit Amount', accessor: 'debit', resizable: false, sortable: false, visible: true, initialWidthPercentage: 7.5, },
    { Header: 'Credit Amount', accessor: 'credit', resizable: false, sortable: false, visible: true, initialWidthPercentage: 7.5, },
    { Header: 'PS', accessor: 'pShare', resizable: false, sortable: false, visible: true, initialWidthPercentage: 2, },
    { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: false, initialWidthPercentage: 1.62, },
    { Header: '', accessor: 'chgType', resizable: false, sortable: false, visible: false, initialWidthPercentage: 0 },
  ]
};

export const crdbLineItemsWH:TableSettings = {
  tableVersion: 1018,
  headersColor: 'white',

  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '320px',
  useStorage: true,
  flexResizer: true,
  columns: [
    { Header: '#', accessor: 'indicator', resizable: false, sortable: false, visible: true, width:30, },
    { Header: 'Billing Type', accessor: 'chgTypeDesc', resizable: false, sortable: false, visible: true, width:74, },
    { Header: 'Billing Code*', accessor: 'crDbCodeView', resizable: false, sortable: false, visible: true, width:137, },
    { Header: 'Description', accessor: 'description', resizable: false, sortable: false, visible: true, width:193, },
    { Header: 'Cur', accessor: 'cur', resizable: false, sortable: false, visible: true, width:50, },
    { Header: 'Rate', accessor: 'rate', resizable: false, sortable: false, visible: true, width: 80, },
    { Header: 'Qty', accessor: 'qty', resizable: false, sortable: false, visible: true, width: 80, },
    { Header: 'Debit Amount', accessor: 'debit', resizable: false, sortable: false, visible: true, width:89, },
    { Header: 'Credit Amount', accessor: 'credit', resizable: false, sortable: false, visible: true, width:89, },
    { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, width: 17 },
  ]
};

export const hblHblBodyLineItems:TableSettings = {
  tableVersion: 10042,
  overflowX: 'hidden',
  overflowY: 'hidden',
  
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  columns: [
    {
      Header: 'Mark and Numbers',
      accessor: 'markAndNumbers',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 17.56,
    },
    {
      Header: 'Package(s)',
      accessor: 'numberOfPackages',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 17.56,
    },
    {
      Header: 'Description of Goods',
      accessor: 'descriptionOfGoods',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 29.72,
    },
    {
      Header: 'Gross Weight',
      accessor: 'grossWeight',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 17.56,
    },
    {
      Header: 'Measurement',
      accessor: 'measurment',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 17.56,
    },
  ]
};

export const aiHawbHblBodyLineItems:TableSettings = {
  tableVersion: 10045,
  overflowX: 'hidden',
  overflowY: 'hidden',
  
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  columns: [
    {
      Header: 'Mark and Numbers',
      accessor: 'markAndNumbers',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 17.57,
    },
    {
      Header: 'Package(s)',
      accessor: 'numberOfPackages',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 17.57,
    },
    {
      Header: 'Description of Goods',
      accessor: 'descriptionOfGoods',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 29.73,
    },
    {
      Header: 'Gross Weight',
      accessor: 'grossWeight',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 17.57,
    },
    {
      Header: 'Volume Weight',
      accessor: 'measurment',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 17.57,
    },
  ]
};

export const codeOceanMoveTypeList:TableSettings = {
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: false,

  headersColor: 'teal',
  alternateRows: true,
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: false,
      sortable: false,
      canBeHidden: false,
      visible: true,
      width: 30,
      //hiddenInColumnSetup: true,
    },
    {
      Header: 'Move Type',
      accessor: 'moveType',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 100,
    },
    {
      Header: 'Description',
      accessor: 'description',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 200,
    },
    {
      Header: 'Inactive',
      accessor: 'inactive',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 70,
    },
  ]
};

export const hblOiHblPoBodyLineItems:TableSettings = {
  tableVersion: 1011,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '300px',
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  columns: [
    {
      Header: '#',
      accessor: 'indicator',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.68,
    },
    {
      Header: 'Container #',
      accessor: 'containerNo',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.93,
    },
    {
      Header: 'P.O #',
      accessor: 'poNo',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.14,
    },
    {
      Header: 'SKU',
      accessor: 'skuNo',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.51,
    },
    {
      Header: 'HTS Code',
      accessor: 'htsCode',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.34,
    },
    {
      Header: 'C/O',
      accessor: 'coo',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.14,
    },
    {
      Header: 'Description',
      accessor: 'description',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 13.39,
    },
    {
      Header: 'Qty',
      accessor: 'shipQty',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.93,
    },
    {
      Header: 'Unit',
      accessor: 'shipUnit',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.14,
    },
    {
      Header: 'Unit Price',
      accessor: 'uPrice',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.14,
    },
    {
      Header: 'KGS',
      accessor: 'kgs',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.93,
    },
    {
      Header: 'CBM',
      accessor: 'cbm',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.93,
    },
    {
      Header: '',
      accessor: 'scroll',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 1.80,
    },
  ]
};

export const hblHblReviewProfitLineItems:TableSettings = {
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '350px',
  useStorage: true,
  flexResizer: true,

  columns: [
    {
      Header: 'M/H',
      accessor: 'mh',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.07,
    },
    {
      Header: 'Date',
      accessor: 'date',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.89,
    },
    {
      Header: 'B/L # / INV #',
      accessor: 'blInvNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.74,
    },
    {
      Header: 'BILLING CODE',
      accessor: 'billingCode',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 12.22,
    },
    {
      Header: 'CUR',
      accessor: 'cur',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.08,
    },
    {
      Header: 'DEF. SALES',
      accessor: 'defSales',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10,
    },
    {
      Header: 'DEF. COST',
      accessor: 'defCost',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10,
    },
    {
      Header: 'DEF. AGENT SALES',
      accessor: 'defAgentSales',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10,
    },
    {
      Header: 'DEF. AGENT COST',
      accessor: 'defAgentCost',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10,
    },
    {
      Header: 'AMOUNT (USD)',
      accessor: 'usdAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10,
    },
    {
      Header: 'EST. PROFIT (USD)',
      accessor: 'estUsdProfit',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10,
    },
  ]
};

export const commonCompaniesCompanyList:TableSettings = {
  tableVersion: 10055,
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: false,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    {
      Header: 'S',
      accessor:'status',
      resizable: false,
      sortable: false,
      canBeHidden: false,
      visible: true,
      width: 30,
    },
    {
      Header: 'Inactive',
      accessor:'inactive',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 54,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Company Name',
      accessor:'shortName',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 200,
      defaultColumnFocus: true,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Company Ref. #',
      accessor:'companyRefNo',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 120,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Contact',
      accessor:'contact',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 100,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Email',
      accessor:'email',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 150,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Phone #',
      accessor:'phone',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'CR Term',
      accessor:'terms',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 50,
      filterable: true, filterType: 'number'
    },
    {
      Header: 'CR Limit',
      accessor:'creditLimit',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 50,
      filterable: true, filterType: 'float'
    },
    {
      Header: 'CR Hold',
      accessor:'creditHold',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 50,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'ID Type',
      accessor:'idType',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Tax ID #',
      accessor:'taxId',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Bond #',
      accessor:'bondNo',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'IATA #',
      accessor:'iataNo',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Bank Account #',
      accessor:'accountNo',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 100,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Salesperson',
      accessor:'salesPerson',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Cust. SVC Rep',
      accessor:'csr',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Broker',
      accessor:'brokerName',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Full Name',
      accessor:'fullName',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 200,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Fax',
      accessor:'fax',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Address',
      accessor:'address',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 150,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'City',
      accessor:'city',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 93,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'State',
      accessor:'state',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 47,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Zip',
      accessor:'zipCode',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 50,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Country',
      accessor:'country',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 50,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Local Name',
      accessor:'localName',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 100,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Local Address',
      accessor:'localAddress',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 100,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Group',
      accessor:'groupId',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 50,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Bill To',
      accessor:'billTo',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true, filterType: 'text'
    },    {
      Header: 'Create',
      accessor:'createdAt',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 100,
      filterable: true, filterType: 'date'
    },
    {
      Header: 'Create By',
      accessor:'u1ID',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Update',
      accessor:'u2Date',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 100,
      filterable: true, filterType: 'date'
    },
    {
      Header: 'Update By',
      accessor:'u2ID',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true, filterType: 'text'
    },
  ]
};

export const commonCompanyBranchesList:TableSettings = {
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: false,

  headersColor: 'teal',
  alternateRows: true,
  columns: [
    {
      Header: '',
      accessor:'indicator',
      resizable: false,
      sortable: false,
      canBeHidden: false,
      visible: true,
      width: 30,
      //hiddenInColumnSetup: true,
    },
    {
      Header: 'Branch',
      accessor:'br',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Branch Name',
      accessor:'branchName',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 125,
      defaultColumnFocus: true,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Contact',
      accessor:'contact',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 125,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Phone #',
      accessor:'phoneNo',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 125,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Fax #',
      accessor:'faxNo',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 125,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'IATA Code',
      accessor:'iataCode',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 125,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'HQ Branch',
      accessor:'hq',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 125,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Update Date',
      accessor:'updatedDate',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 125,
      filterable: true, filterType: 'date'
    },
    {
      Header: 'Update By',
      accessor:'updateBy',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 125,
      filterable: true, filterType: 'text'
    },
    {
      Header: 'Create By',
      accessor:'createdBy',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 125,
      filterable: true, filterType: 'text'
    },
  ]
};

export const codeOceanContainerTypeList:TableSettings = {
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: false,

  headersColor: 'teal',
  alternateRows: true,
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: false,
      sortable: false,
      canBeHidden: false,
      visible: true,
      width: 30,
      //hiddenInColumnSetup: true,
    },
    {
      Header: 'Container Type',
      accessor: 'containerType',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 100,
    },
    {
      Header: 'Description',
      accessor: 'description',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 200,
    },
    {
      Header: 'Group',
      accessor: 'group',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 200,
    },
    {
      Header: 'ISO Type',
      accessor: 'isoSizeAndType',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 200,
    },
    {
      Header: 'Length',
      accessor: 'length',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 200,
    },
    {
      Header: 'Height',
      accessor: 'height',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 200,
    },
    {
      Header: 'Type',
      accessor: 'type',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 200,
    },
    {
      Header: 'Inactive',
      accessor: 'inactive',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 70,
    },
    {
      Header: 'Update Date',
      accessor: 'updateDate',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 200,
    },
    {
      Header: 'Update By',
      accessor: 'updatedBy',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 200,
    },
    {
      Header: 'Created By',
      accessor: 'createdBy',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 200,
    },
  ]
};

/**
 * SalesCostEntry - BillAccordion
 */
export const billInsideAccordionLineItems:TableSettings = {
  tableVersion: 10044,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  height: '100%',
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.63,
    },
    {
      Header: 'Action',
      accessor: 'action',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.14,
    },
    {
      Header: 'Bill #',
      accessor: 'billNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.77,
    },
    {
      Header: 'Bill Date',
      accessor: 'billDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Vendor',
      accessor: 'vendorName',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 14.04,
    },
    {
      Header: 'RCVD',
      accessor: 'billReceived',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'Due Date',
      accessor: 'dueDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Hold',
      accessor: 'hold',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'Draft',
      accessor: 'draft',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'CUR',
      accessor: 'currency',   //[save-sqlinfo]'cur',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'Bill Amount',
      accessor: 'billAmt',    //[save-sqlinfo]'billAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Paid Amount',
      accessor: 'paidAmt',    //[save-sqlinfo]'paidAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Balance',
      accessor: 'balanceDue',   //[save-sqlinfo]'balanceAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Paid',
      accessor: 'paid',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.02,
    },
    {
      Header: 'CrDb #',
      accessor: 'crDbNo',   //[save-sqlinfo]'relatedCrdbNos',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.02,
    },
    {
      Header: '',
      accessor: 'unfold',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 1.72, // Make sure to have enough space for scrollbar
    },
  ]
};

export const billInsideAccordionLineItems_noCrDb:TableSettings = {
  tableVersion: 10042,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  height: '100%',
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.63,
    },
    {
      Header: 'Action',
      accessor: 'action',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.14,
    },
    {
      Header: 'Bill No',
      accessor: 'billNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.77,
    },
    {
      Header: 'Bill Date',
      accessor: 'billDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Vendor',
      accessor: 'vendorName',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.53,
    },
    {
      Header: 'Credit Card',
      accessor: 'ccName',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.53,
    },
    {
      Header: 'RCVD',
      accessor: 'billReceived',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'Due Date',
      accessor: 'dueDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Hold',
      accessor: 'hold',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'Draft',
      accessor: 'draft',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'CUR',
      accessor: 'currency',   //[save-sqlinfo]'cur',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'Bill Amount',
      accessor: 'billAmt',    //[save-sqlinfo]'billAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Paid Amount',
      accessor: 'paidAmt',    //[save-sqlinfo]'paidAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Balance',
      accessor: 'balanceDue',   //[save-sqlinfo]'balanceAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Paid Date',
      accessor: 'lastPaidDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: '',
      accessor: 'unfold',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 1.72, // Make sure to have enough space for scrollbar
    },
  ]
};

export const billInsideAccordionLineItems_noCrDbWithDo:TableSettings = {
  tableVersion: 10046,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  height: '100%',
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.63,
    },
    {
      Header: 'Action',
      accessor: 'action',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.14,
    },
    {
      Header: 'Bill #',
      accessor: 'billNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.77,
    },
    {
      Header: 'Bill Date',
      accessor: 'billDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'PRO #',
      accessor: 'orderNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 5.5,
    },
    {
      Header: 'Vendor',
      accessor: 'vendorName',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.78,
    },
    {
      Header: 'Credit Card',
      accessor: 'ccName',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.78,
    },
    {
      Header: 'RCVD',
      accessor: 'billReceived',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'Due Date',
      accessor: 'dueDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Hold',
      accessor: 'hold',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'Draft',
      accessor: 'draft',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'CUR',
      accessor: 'currency',   //[save-sqlinfo]'cur',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'Bill Amount',
      accessor: 'billAmt',    //[save-sqlinfo]'billAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Paid Amount',
      accessor: 'paidAmt',    //[save-sqlinfo]'paidAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Balance',
      accessor: 'balanceDue',   //[save-sqlinfo]'balanceAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Paid Date',
      accessor: 'lastPaidDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: '',
      accessor: 'unfold',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 1.72, // Make sure to have enough space for scrollbar
    },
  ]
};

/**
 * SalesCostEntry - InvoiceAccordion
 */
export const invoiceInsideAccordionLineItems:TableSettings = {
  tableVersion: 10044,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  height: '100%',
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.63,
    },
    {
      Header: 'Action',
      accessor: 'action',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.14,
    },
    {
      Header: 'Invoice #',
      accessor: 'invoiceNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.77,
    },
    {
      Header: 'Invoice Date',
      accessor: 'postDate',   //[save-sqlinfo]'invoiceDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Customer',
      accessor: 'invoiceToName',    //[save-sqlinfo]'invoiceTo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 14.04,
    },
    {
      Header: 'XMT',
      accessor: 'transmitted',    //[save-sqlinfo]'xmt',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'Due Date',
      accessor: 'dueDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Over',
      accessor: 'overdue',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'Draft',
      accessor: 'draft',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'CUR',
      accessor: 'currency',   //[save-sqlinfo]'cur',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'Invoice Amount',
      accessor: 'invoiceAmt',   //[save-sqlinfo]'invoiceAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Paid Amount',
      accessor: 'paidAmt',    //[save-sqlinfo]'paidAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Balance',
      accessor: 'balanceAmt',   //[save-sqlinfo]'balanceAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Paid',
      accessor: 'paid',   //[save-sqlinfo]'lastPaidDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.02,
    },
    {
      Header: 'CrDb #',
      accessor: 'crDbNo',   //[save-sqlinfo]'relatedCrdbNos',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.02,
    },
    {
      Header: '',
      accessor: 'unfold',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 1.72, // Make sure to have enough space for scrollbar
    },
  ]
};

export const invoiceInsideAccordionLineItems_noCrDb:TableSettings = {
  tableVersion: 10041,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  height: '100%',
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.63,
    },
    {
      Header: 'Action',
      accessor: 'action',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.14,
    },
    {
      Header: 'Invoice No',
      accessor: 'invoiceNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.77,
    },
    {
      Header: 'Invoice Date',
      accessor: 'postDate',   //[save-sqlinfo]'invoiceDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Customer',
      accessor: 'invoiceToName',    //[save-sqlinfo]'invoiceTo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 21.06,
    },
    {
      Header: 'XMT',
      accessor: 'transmitted',    //[save-sqlinfo]'xmt',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'Due Date',
      accessor: 'dueDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Over',
      accessor: 'overdue',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'Draft',
      accessor: 'draft',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'CUR',
      accessor: 'currency',   //[save-sqlinfo]'cur',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.39,
    },
    {
      Header: 'Invoice Amount',
      accessor: 'invoiceAmt',   //[save-sqlinfo]'invoiceAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Paid Amount',
      accessor: 'paidAmt',    //[save-sqlinfo]'paidAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Balance',
      accessor: 'balanceAmt',   //[save-sqlinfo]'balanceAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: 'Paid Date',
      accessor: 'paidDate',   //[save-sqlinfo]'lastPaidDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.02,
    },
    {
      Header: '',
      accessor: 'unfold',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 1.72, // Make sure to have enough space for scrollbar
    },
  ]
};

/**
 * SalesCostEntry - CrDbAccordion
 */
export const crdbInsideAccordionLineItems:TableSettings = {
  tableVersion: 10046,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  height: '100%',
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.63,
    },
    {
      Header: 'Action',
      accessor: 'action',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.14,
    },
    {
      Header: 'CrDb #',
      accessor: 'crDbNo',   //[save-sqlinfo]'crdbNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.77,
    },
    {
      Header: 'CrDb Date',
      accessor: 'crDbDate',   //'crdbDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.01,
    },
    {
      Header: 'Agent',
      accessor: 'agentName',    //[save-sqlinfo]'agent',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 14.04,
    },
    {
      Header: 'XMT',
      accessor: 'printed',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.38,
    },   
    {
      Header: 'CFM',
      accessor: 'isConfirm',   //[save-sqlinfo]'cfm',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.38,
    },

    {
      Header: 'Draft',
      accessor: 'draft',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.38,
    },
    {
      Header: 'CUR',
      accessor: 'currency',   //[save-sqlinfo]'cur',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.50,
    },
    {
      Header: 'Debit',
      accessor: 'debitAmt',   //[save-sqlinfo]'debitAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.14,
    },
    {
      Header: 'Credit',
      accessor: 'creditAmt',    //[save-sqlinfo]'creditAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.14,
    },
    {
      Header: 'CrDb Amount',
      accessor: 'totalAmt',  //[save-sqlinfo]'crdbAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.14,
    },
    {
      Header: 'Paid Amount',
      accessor: 'paidAmt',    //[save-sqlinfo]'paidAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.14,
    },
    {
      Header: 'Balance',
      accessor: 'balAmt',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.14,
    },
    {
      Header: 'Paid',
      accessor: 'paid',   //[save-sqlinfo]'lastPaidDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.14,
    },
    {
      Header: 'B/L #',
      accessor: 'blNo',   //[save-sqlinfo]'relatedBlNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.14,
    },
    {
      Header: '',
      accessor: 'unfold',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 1.75, // Make sure to have enough space for scrollbar
    },
  ]
};

/*
Acctinfo - Bill
*/ 
export const acctInfoBillLineItems:TableSettings = {
  tableVersion: 10043,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  height: '100%',
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: false,
      sortable: false,
      visible: true,
      width: 30,
    }, 
    {
      Header: 'Date',
      accessor: 'postDate',
      resizable: true,
      sortable: false,
      visible: true,
      width: 80,
    },
    {
      Header: 'Vendor',
      accessor: 'entityName',
      resizable: true,
      sortable: false,
      visible: true,
      width: 80,
    },
   
    {
      Header: 'Description',
      accessor: 'descript',
      resizable: true,
      sortable: false,
      visible: true,
      width: 70,
    },
    {
      Header: 'Bill #',
      accessor: 'invoiceNo',
      resizable: true,
      sortable: false,
      visible: true,
      width: 60,
    },
    {
      Header: 'CUR',
      accessor: 'cur',
      resizable: true,
      sortable: false,
      visible: true,
      width: 50,
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      resizable: true,
      sortable: false,
      visible: true,
      width: 80,
    },
    {
      Header: 'Due Date',
      accessor: 'dueDate',
      resizable: true,
      sortable: false,
      visible: true,
      width: 80,
    },
    {
      Header: 'Balance',
      accessor: 'balance',
      resizable: true,
      sortable: false,
      visible: true,
      width: 80,
    },
    {
      Header: 'Status',
      accessor: 'status',  
      resizable: true,
      sortable: false,
      visible: true,
      width: 50,
    }
  ]
};

/*
Acctinfo - Invoice
*/ 
export const acctInfoInvoiceLineItems:TableSettings = {
  tableVersion: 10043,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  height: '100%',
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: false,
      sortable: false,
      visible: true,
      width: 30,
    }, 
    {
      Header: 'Date',
      accessor: 'postDate',
      resizable: true,
      sortable: false,
      visible: true,
      width: 80,
    },
    {
      Header: 'Bill To',
      accessor: 'entityName',
      resizable: true,
      sortable: false,
      visible: true,
      width: 110,
    },
   
    {
      Header: 'Invoice #',
      accessor: 'invoiceNo',
      resizable: true,
      sortable: false,
      visible: true,
      width: 90,
    },
    {
      Header: 'CUR',
      accessor: 'cur',
      resizable: true,
      sortable: false,
      visible: true,
      width: 50,
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      resizable: true,
      sortable: false,
      visible: true,
      width: 85,
    },
    {
      Header: 'Due Date',
      accessor: 'dueDate',
      resizable: true,
      sortable: false,
      visible: true,
      width: 80,
    },
    {
      Header: 'Balance',
      accessor: 'balance',
      resizable: true,
      sortable: false,
      visible: true,
      width: 85,
    },
    {
      Header: 'XMIT',
      accessor: 'xmit', 
      resizable: true,
      sortable: false,
      visible: true,
      width: 50,
    }
  ]
};

/*
Acctinfo - CRDB
*/ 
export const acctInfoCrdbLineItems:TableSettings = {
  tableVersion: 10043,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  height: '100%',
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: false,
      sortable: false,
      visible: true,
      width: 30,
    }, 
    {
      Header: 'Date',
      accessor: 'postDate',
      resizable: true,
      sortable: false,
      visible: true,
      width: 80,
    },
    {
      Header: 'Agent',
      accessor: 'entityName',
      resizable: true,
      sortable: false,
      visible: true,
      width: 150,
    },
   
    {
      Header: 'CR/DB #',
      accessor: 'invoiceNo',
      resizable: true,
      sortable: false,
      visible: true,
      width: 80,
    },
    {
      Header: 'CUR',
      accessor: 'cur',
      resizable: true,
      sortable: false,
      visible: true,
      width: 50,
    },
    {
      Header: 'Total Amount',
      accessor: 'amount',
      resizable: true,
      sortable: false,
      visible: true,
      width: 85,
    },
    {
      Header: 'Balance',
      accessor: 'balance',
      resizable: true,
      sortable: false,
      visible: true,
      width: 85,
    },
    {
      Header: 'XMIT',
      accessor: 'xmit',   
      resizable: true,
      sortable: false,
      visible: true,
      width: 50,
    },
    {
      Header: 'CFM',
      accessor: 'cfm',   
      resizable: true,
      sortable: false,
      visible: true,
      width: 50,
    }
  ]
};

export const oceanImportMblList:TableSettings = {
  tableVersion: 10098,
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: false,

  headersColor: 'gray',
  alternateRows: true,
  columns: []
};


export const mblReviewProfit:TableSettings = {
  tableVersion: 10069,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '550px',
  useStorage: true,
  flexResizer: false,

  headersColor: 'lightgray',
  columns: [
    {
      Header: 'M/H',
      accessor: 'indicator',
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.94,
    },
    {
      Header: 'Date',
      accessor: 'date',
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.84,
    },
    {
      Header: 'BL # / Billings',
      accessor: 'no',
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.8,
    },
    {
      Header: 'Company',
      accessor: 'comName',
      sortable: false,
      visible: true,
      initialWidthPercentage: 11.76,
    },
    {
      Header: 'Billing Code',
      accessor: 'billingCode',
      sortable: false,
      visible: true,
      initialWidthPercentage: 14.7,
    },
    {
      Header: 'CUR',
      accessor: 'cur',
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.92,
    },
    {
      Header: 'D. SALES',
      accessor: 'defSales',
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.84,
    },
    {
      Header: 'D. COST',
      accessor: 'defCost',
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.84,
    },
    {
      Header: 'D. A/Sales',
      accessor: 'defAgentSales',
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.84,
    },
    {
      Header: 'D. A/Cost',
      accessor: 'defAgentCost',
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.84,
    },
    {
      Header: 'AMOUNT (USD)',
      accessor: 'primaryAmount',
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.84,
    },
    {
      Header: 'E. PROFIT (USD)',
      accessor: 'estProfit',
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.84,
    },
    {
      Header: ' ',
      accessor: 'scroll',
      sortable: false,
      visible: true,
      initialWidthPercentage: 1.96,
    },
  ]
};

/**
 * pt241218: Add Company and adjust percentage
 */
export const whReviewProfit:TableSettings = {
  tableVersion: 10067,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '550px',
  useStorage: true,
  flexResizer: false,

  headersColor: 'lightgray',
  columns: [
    {
      Header: 'Date',
      accessor: 'date',
      sortable: false,
      visible: true,
      initialWidthPercentage: 8,
    },
    {
      Header: 'BL # / Billings',
      accessor: 'no',
      sortable: false,
      visible: true,
      initialWidthPercentage: 10, //16,
    },
    {
      Header: 'Company',
      accessor: 'comName',
      sortable: false,
      visible: true,
      initialWidthPercentage: 13,
    },
    {
      Header: 'BILLING CODE',
      accessor: 'billingCode',
      sortable: false,
      visible: true,
      initialWidthPercentage: 15, //18,
    },
    {
      Header: 'CUR',
      accessor: 'cur',
      sortable: false,
      visible: true,
      initialWidthPercentage: 5,
    },
    {
      Header: 'SALES',
      accessor: 'defSales',
      sortable: false,
      visible: true,
      initialWidthPercentage: 11.5, //12.5,
    },
    {
      Header: 'COST',
      accessor: 'defCost',
      sortable: false,
      visible: true,
      initialWidthPercentage: 11.5, //12.5,
    },
    {
      Header: 'AMOUNT (USD)',
      accessor: 'primaryAmount',
      sortable: false,
      visible: true,
      initialWidthPercentage: 11.5, //12.5,
    },
    {
      Header: 'G. PROFIT (USD)',
      accessor: 'estProfit',
      sortable: false,
      visible: true,
      initialWidthPercentage: 11.5, //12.5,
    },
    {
      Header: ' ',
      accessor: 'scroll',
      sortable: false,
      visible: true,
      initialWidthPercentage: 3,
    },
  ]
};

export const btReviewProfit:TableSettings = {
  tableVersion: 10070,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '550px',
  useStorage: true,
  flexResizer: false,

  headersColor: 'lightgray',
  columns: [
    {
      Header: 'L/M',
      accessor: 'indicator',
      sortable: false,
      visible: true,
      initialWidthPercentage: 3,
    },
    {
      Header: 'Date',
      accessor: 'date',
      sortable: false,
      visible: true,
      initialWidthPercentage: 8,
    },
    {
      Header: 'BL No. / Billings',
      accessor: 'no',
      sortable: false,
      visible: true,
      initialWidthPercentage: 10, //16,
    },
    {
      Header: 'Company',
      accessor: 'comName',
      sortable: false,
      visible: true,
      initialWidthPercentage: 13,
    },
    {
      Header: 'BILLING CODE',
      accessor: 'billingCode',
      sortable: false,
      visible: true,
      initialWidthPercentage: 14, //18,
    },
    {
      Header: 'CUR',
      accessor: 'cur',
      sortable: false,
      visible: true,
      initialWidthPercentage: 5,
    },
    {
      Header: 'SALES',
      accessor: 'defSales',
      sortable: false,
      visible: true,
      initialWidthPercentage: 11, //12.5,
    },
    {
      Header: 'COST',
      accessor: 'defCost',
      sortable: false,
      visible: true,
      initialWidthPercentage: 11, //12.5,
    },
    {
      Header: 'AMOUNT (USD)',
      accessor: 'primaryAmount',
      sortable: false,
      visible: true,
      initialWidthPercentage: 11, //12.5,
    },
    {
      Header: 'PROFIT (USD)',
      accessor: 'estProfit',
      sortable: false,
      visible: true,
      initialWidthPercentage: 11, //12.5,
    },
    {
      Header: ' ',
      accessor: 'scroll',
      sortable: false,
      visible: true,
      initialWidthPercentage: 3,
    },
  ]
};

export const invoiceList:TableSettings = {
  tableVersion: 10064,
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: false,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
  ]
};

export const billList:TableSettings = {
  tableVersion: 10061,
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: false,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'status', Header: 'S', visible: true, width: 30, canBeHidden: false, resizable: false, sortable: false, },
    { accessor:'draft', Header: 'D', visible: true, width: 30, canBeHidden: false, resizable: false, sortable: false, filterable: true, filterType: 'text'},
    { accessor:'recognized', Header: 'R', visible: true, width: 35, canBeHidden: false, resizable: false, sortable: true, filterable: true, filterType: 'text' },
    // { accessor:'void', Header: 'V', visible: true, width: 35, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'billDate', Header: 'Date', visible: true, width: 75, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'date' },
    { accessor:'billNo', Header: 'Bill #', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, },
    { accessor:'branch', Header: 'BR', visible: true, width: 40, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'vendorName', Header: 'Vendor', visible: true, width: 150, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'dueDate', Header: 'Due Date', visible: true, width: 75, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'date' },
    { accessor:'currency', Header: 'CUR', visible: true, width: 40, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'amount', Header: 'Amount', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'float' },
    { accessor:'paidAmount', Header: 'Paid', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'float' },
    { accessor:'balance', Header: 'Balance', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'float' },
    { accessor:'confirmed', Header: 'Confirmed', visible: true, width: 60, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text'},
    { accessor:'draftPaid', Header: 'Draft Paid', visible: true, width: 60, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text', tooltip: 'Draft Paid' },
    { accessor:'checkNo', Header: 'Paid Check #', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'vendorRefNo', Header: 'Vendor Ref. #', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'paidDate', Header: 'Paid Date', visible: true, width: 75, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'date' },
    { accessor:'lastClearDate', Header: 'Chk Clear Date', visible: true, width: 75, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'date' },
    { accessor:'invoiced', Header: 'B. Rcvd.', visible: true, width: 50, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text', tooltip: 'Bill Received' },
    { accessor:'hold', Header: 'P. Hold', visible: true, width: 50, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text', tooltip: 'Payment Hold' }, // ????
    { accessor:'approved', Header: 'Apprvd', visible: true, width: 50, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text', tooltip: 'Bill Approved' }, // ????
    // { accessor:'appPaidDate', Header: 'Sch. Date', visible: true, width: 85, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'date', tooltip: 'Scheduled Date' }, // ????
    { accessor:'jobType', Header: 'Type', visible: true, width: 40, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'refNo', Header: 'Ref. #', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'mblNo', Header: 'MBL/MAWB #', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' }, // ????
    { accessor:'hblNo', Header: 'HBL/HWAB #', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' }, // ????
    { accessor:'remarks', Header: 'Gen. Remark', visible: true, width: 120, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text', tooltip: 'General Remark' }, // ????
    { accessor:'updatedDate', Header: 'Update', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'date' },
    { accessor:'updatedBy', Header: 'Update By', visible: true, width: 80, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'createdBy', Header: 'Create By', visible: true, width: 80, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
  ]
};

export const crdbList:TableSettings = {
  tableVersion: 10066,
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: false,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'status', Header: 'S', visible: true, width: 30, canBeHidden: false, resizable: false, sortable: false, },
    { accessor:'draft', Header: 'D', visible: true, width: 30, canBeHidden: false, resizable: false, sortable: false, filterable: true, filterType: 'text'},
    // { accessor:'recognized', Header: 'R', visible: true, width: 35, canBeHidden: false, resizable: false, sortable: true, filterable: true, filterType: 'text' },
    // { accessor:'void', Header: 'V', visible: true, width: 35, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text'},
    { accessor:'invoiceDate', Header: 'Date', visible: true, width: 75, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'date'},
    { accessor:'crDbNo', Header: 'Crdb #', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true,},
    { accessor:'branch', Header: 'BR', visible: true, width: 40, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text'},
    { accessor:'agent', Header: 'Agent', visible: true, width: 150, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text'},
    { accessor:'yourRef', Header: 'Agent Ref. #', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text'},
    { accessor:'currency', Header: 'CUR', visible: true, width: 40, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text'},
    { accessor:'debitTotal', Header: 'Debit Amount', visible: false, width: 120, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'float'},
    { accessor:'creditTotal', Header: 'Credit Amount', visible: false, width: 120, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'float'},
    { accessor:'total', Header: 'Amount', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'float'},
    { accessor:'paidAmt', Header: 'Paid', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'float'},
    { accessor:'balance', Header: 'Balance', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'float'},
    { accessor:'settled', Header: 'Confirmed', visible: true, width: 60, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text'},
    { accessor:'draftPaid', Header: 'Draft Paid', visible: true, width: 60, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text', tooltip: 'Draft Paid' },
    { accessor:'checkNo', Header: 'Paid Check #', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'paidDate', Header: 'Paid Date', visible: true, width: 75, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'date'},
    { accessor:'lastClearDate', Header: 'Chk Clear Date', visible: true, width: 75, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'date'},
    { accessor:'jobType', Header: 'Type', visible: true, width: 40, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text'},
    { accessor:'refNo', Header: 'Ref. #', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text'},
    { accessor:'mblNo', Header: 'MBL/MAWB #', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' }, // ????
    { accessor:'hblNo', Header: 'HBL/HWAB #', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' }, // ????
    { accessor:'u2Date', Header: 'Update', visible: true, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'date'},
    { accessor:'u2ID', Header: 'Update By', visible: true, width: 80, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text'},
    { accessor:'createBy', Header: 'Create By', visible: true, width: 80, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text'},
  ]
};

export const codesAccountingCurrencyList:TableSettings = {
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: false,

  headersColor: 'teal',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, width: 30, canBeHidden: false, resizable: false, sortable: false, },
    { accessor:'currencyCode', Header: 'Currency Code', visible: true, width: 125, canBeHidden: true, resizable: true, sortable: true, },
    { accessor:'decimalDigit', Header: 'Decimal Digit', visible: true, width: 125, canBeHidden: true, resizable: true, sortable: true, },
    { accessor:'symbol', Header: 'Symbol', visible: true, width: 125, canBeHidden: true, resizable: true, sortable: true, },
    { accessor:'localSymbol', Header: 'Local Symbol', visible: true, width: 125, canBeHidden: true, resizable: true, sortable: true, },
    { accessor:'inactive', Header: 'Inactive', visible: true, width: 125, canBeHidden: true, resizable: true, sortable: true, },
  ]
};

export const oceanImportHblList:TableSettings = {
  tableVersion: 10078,
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: false,

  headersColor: 'gray',
  alternateRows: true,
  columns: []
};

export const codesCompanyCompanyUserList:TableSettings = {
  tableVersion: 10047,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 3.64, },
    { accessor:'userId', Header: 'User ID', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 9.09,},
    { accessor:'userName', Header: 'Name', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 14.55, },
    { accessor:'eMail', Header: 'Email Address', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 18.18, },
    { accessor:'subscriberId', Header: 'Subscriber ID', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 18.18, },
    { accessor:'branch', Header: 'BR', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 5.45, },
    { accessor:'dept', Header: 'Department', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 7.27, },
    { accessor:'securityGroup', Header: 'Security Group', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 7.27, },
    { accessor:'inactive', Header: 'Inactive', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'date',initialWidthPercentage: 5.45, },
    { accessor:'updateDate', Header: 'Updated Date', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'date',initialWidthPercentage: 9.09 },
    { accessor:'scroll', Header: '', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'date',initialWidthPercentage: 1.83 },
  ]
};

export const subscriberIdList:TableSettings = {
  tableVersion: 10044,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 5.56, },
    { accessor:'subscriberId', Header: 'Subscriber ID', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 27.78, },
    { accessor:'userId', Header: 'User ID', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 13.89,},
    { accessor:'email', Header: 'Email', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 27.78, },
    { accessor:'createDate', Header: 'Create Date', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 13.89, },
    { accessor:'logIn', Header: 'Login', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'bool',initialWidthPercentage: 8.33, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 2.77, },
  ]
};

export const codesCompanyCompanyDepartmentList:TableSettings = {
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: false,

  headersColor: 'teal',
  alternateRows: true,
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: false,
      sortable: false,
      canBeHidden: false,
      visible: true,
      width: 30,
      //hiddenInColumnSetup: true,
    },
    {
      Header: 'Name',
      accessor: 'name',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true,
      filterType: 'text',
      defaultColumnFocus: true,
    },
    {
      Header: 'Description',
      accessor: 'description',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 200,
      filterable: true,
      filterType: 'text',
    },
    {
      Header: 'Inactive',
      accessor: 'inactive',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true,
      filterType: 'text',
    },
  ]
};

export const codeOceanPortCodeList:TableSettings = {
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: false,

  headersColor: 'teal',
  alternateRows: true,
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: false,
      sortable: false,
      canBeHidden: false,
      visible: true,
      width: 30,
      //hiddenInColumnSetup: true,
    },
    {
      Header: 'Code',
      accessor: 'code',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true,
      filterType: 'text',
      defaultColumnFocus: true,
    },
    {
      Header: 'Port Name',
      accessor: 'portName',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 200,
      filterable: true,
      filterType: 'text',
    },
    {
      Header: 'CY Location',
      accessor: 'cy',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 200,
      filterable: true,
      filterType: 'text',
    },
    {
      Header: 'CFS Location',
      accessor: 'cfs',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 200,
      filterable: true,
      filterType: 'text',
    },
  ]
};

export const codeOceanVesselList:TableSettings = {
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: false,

  headersColor: 'teal',
  alternateRows: true,
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: false,
      sortable: false,
      canBeHidden: false,
      visible: true,
      width: 30,
      //hiddenInColumnSetup: true,
    },
    {
      Header: 'Vessel Name',
      accessor: 'vesselName',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 250,
      filterable: true,
      filterType: 'text',
      defaultColumnFocus: true,
    },
    {
      Header: 'Country',
      accessor: 'country',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true,
      filterType: 'text',
    },
    {
      Header: 'Vessel Flag',
      accessor: 'vesselFlag',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true,
      filterType: 'text',
    },
    {
      Header: 'Lloyds',
      accessor: 'lloyds',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true,
      filterType: 'text',
    },
    {
      Header: 'Group',
      accessor: 'vgroup',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 80,
      filterable: true,
      filterType: 'text',
    },
  ]
};

export const oceanImportQuotationsList:TableSettings = {
  tableVersion: 1002,
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: false,

  headersColor: 'teal',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, width: 30, canBeHidden: false, resizable: false, sortable: false, },
    { accessor:'qutotationDate', Header: 'Date', visible:true, width: 124, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'date', defaultColumnFocus: true, },
    { accessor:'quotationNo', Header: 'Quotation #', visible:true, width: 124, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'status', Header: 'Status', visible:true, width: 90, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'moveType', Header: 'Move Type', visible:true, width: 124, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'customer', Header: 'Customer', visible:true, width: 124, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'customerRefNo', Header: 'Customer Ref. #', visible:true, width: 127, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'contact', Header: 'Contact', visible:true, width: 127, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'validBy', Header: 'Valid By', visible:true, width: 127, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'date' },
    { accessor:'quoteBy', Header: 'Quote By', visible:true, width: 127, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'commodity', Header: 'Commodity', visible:true, width: 127, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'type', Header: 'Type', visible:true, width: 90, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'invoiceNo', Header: 'Invoice #', visible:true, width: 127, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'placeOfReceipt', Header: 'Place of Receipt', visible:true, width: 210, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'location' },
    { accessor:'loadingPort', Header: 'Loading Port', visible:true, width: 210, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'location' },
    { accessor:'dischargePort', Header: 'Discharge Port', visible:true, width: 210, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'location' },
    { accessor:'placeOfDelivery', Header: 'Place of Delivery', visible:true, width: 210, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'location' },
    { accessor:'salesperson', Header: 'Salesperson', visible:true, width: 127, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'update', Header: 'Update', visible:true, width: 150, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'date'  },
    { accessor:'updated', Header: 'Update by', visible:true, width: 150, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text'  },
    { accessor:'created', Header: 'Create By', visible:true, width: 150, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text'  },
  ]
};

export const oiQuotationLineItems:TableSettings = {
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '350px',
  useStorage: true,
  flexResizer: true,
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.53,
    },
    {
      Header: 'Billing Code',
      accessor: 'billingCode',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 18.72,
    },
    {
      Header: 'Item Description',
      accessor: 'itemDescription',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 21.72,
    },
    {
      Header: 'CUR',
      accessor: 'cur',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.53,
    },
    {
      Header: 'Rate',
      accessor: 'rate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 16.29,
    },
    {
      Header: 'Qty',
      accessor: 'qty',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.69,
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 12.49,
    },
    {
      Header: 'USD Amount',
      accessor: 'headerAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 12.03,
    },
    {
      Header: '',
      accessor: 'commands',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3,
    },
  ]
};

export const oiQuotationWithVatLineItems:TableSettings = {
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '350px',
  useStorage: true,
  flexResizer: true,
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.53,
    },
    {
      Header: 'Billing Code',
      accessor: 'billingCode',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 18.72,
    },
    {
      Header: 'Item Description',
      accessor: 'itemDescription',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 21.22,
    },
    {
      Header: 'CUR',
      accessor: 'cur',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.53,
    },
    {
      Header: 'Rate',
      accessor: 'rate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 15.29,
    },
    {
      Header: 'Qty',
      accessor: 'qty',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.69,
    },
    {
      Header: 'VAT',
      accessor: 'useVat',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.5,
    },
    
    {
      Header: 'Amount',
      accessor: 'amount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 11.49,
    },
    {
      Header: 'USD Amount',
      accessor: 'headerAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 11.03,
    },
    {
      Header: '',
      accessor: 'commands',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3,
    },
  ]
};

export const infoAreaMemoTable:TableSettings = {
  tableVersion: 1037,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  alternateRows: true,
  columns: [
    { Header: 'Date', accessor: 'updateAt', resizable: true, sortable: false, visible: true, initialWidthPercentage : 19.6},
    { Header: 'Type', accessor: 'jobType', resizable: true, sortable: false, visible: true, initialWidthPercentage :7.4 },
    { Header: 'Subject', accessor: 'title', resizable: true, sortable: false, visible: true, initialWidthPercentage : 35.7},
    { Header: 'Attachment', accessor: 'attachment', resizable: true, sortable: false, visible: true, initialWidthPercentage : 16.9},
    { Header: 'Update By', accessor: 'updateBy', resizable: true, sortable: false, visible: true, initialWidthPercentage : 16.9 },
    { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, initialWidthPercentage : 3.5},
  ]
};

export const infoAreaUpdateHistoryTable:TableSettings = {
  tableVersion: 1016,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '98px',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    {
      Header: 'Date',
      accessor: 'date',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 18.60,
    },
    {
      Header: 'Update History',
      accessor: 'history',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 77.52,
    },
    {
      Header: '',
      accessor: 'scroll',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.88,
    },
  ]
};

export const infoAreaLogHistoryTable:TableSettings = {
  tableVersion: 1032,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    {
      Header: 'Date',
      accessor: 'postDate',   // 'date',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 21.6,
    },
    {
      Header: 'By',
      accessor: 'senderId',   //'userName',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 13.5
    },
    {
      Header: 'Log',
      accessor: 'type',   //'category',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 61.6
    },
    {
      Header: '',
      accessor: 'scroll',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.3
    },
  ]
};

export const infoAreaBicTable:TableSettings = {
  tableVersion: 10060,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '220px',
  width: '1139px',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    {
      Header: 'Billings',
      accessor: 'type',   //'category',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.06,
    },
    {
      Header: 'Draft',
      accessor: 'draft',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.03,
    },
    {
      Header: 'Number',
      accessor: 'invoiceNo',   // 'number',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.5,
    },
    {
      Header: 'Company Name',
      accessor: 'entityName',   //'companyName',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 17.13,
    },
    {
      Header: 'Date',
      accessor: 'postDate',   // 'date',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.85,
    },
    {
      Header: 'CUR',
      accessor: 'cur',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.23,
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.06,
    },
    {
      Header: 'Paid Amount',
      accessor: 'paidAmount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.06,
    },
    {
      Header: 'Balance',
      accessor: 'balance',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.06,
    },
    {
      Header: 'Check #',
      accessor: 'checkNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.5,
    },
    {
      Header: 'M/H',
      accessor: 'jobType',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.03,
    },
    {
      Header: 'B/L #',
      accessor: 'blNo',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 13.71,
    },
    {
      Header: '',
      accessor: 'scroll',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.04,
    },
  ]
};

export const infoAreaHblTable:TableSettings = {
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '160px',
  width: '1408px',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    {
      Header: 'HBL Type',
      accessor: 'hblType',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 13.06,
    },
    {
      Header: 'HBL #',
      accessor: 'hblNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 17.9,
    },
    {
      Header: 'HBL Customer',
      accessor: 'hblCustomer',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 24.78,
    },
    {
      Header: 'Final Destination',
      accessor: 'finalDestination',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 20.05,
    },
    {
      Header: 'F.ETA',
      accessor: 'feta',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.6,
    },
    {
      Header: 'Commodity',
      accessor: 'commodity',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 15.61,
    },
  ]
};

export const infoAreaMblTable:TableSettings = {
  tableVersion: 1002,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '160px',
  width: '1408px',
  useStorage: true,
  flexResizer: true,

  headersColor: 'teal',
  alternateRows: true,
  columns: [
    {
      Header: 'MBL Type',
      accessor: 'mblType',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.31,
    },
    {
      Header: 'MBL #',
      accessor: 'mblNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 14.91,
    },
    {
      Header: 'MBL Agent',
      accessor: 'mblAgent',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 21.12,
    },
    {
      Header: 'Loading Port',
      accessor: 'loadingPort',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 17.39,
    },
    {
      Header: 'ETD',
      accessor: 'etd',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.94,
    },
    {
      Header: 'Discharge Port',
      accessor: 'dischargePort',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 17.39,
    },
    {
      Header: 'ETA',
      accessor: 'eta',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.94,
    },
  ]
};

export const infoAreaBicHistoryTable:TableSettings = {
  tableVersion: 1002,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '160px',
  width: '885px',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    {
      Header: 'Action Date',
      accessor: 'ediDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 13.56,
    },
    {
      Header: 'User',
      accessor: 'ediId',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.21,
    },
    {
      Header: 'Action',
      accessor: 'refNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.21,
    },
    {
      Header: 'CUR',
      accessor: 'currency',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 5.65,
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.6,
    },
    {
      Header: 'Bic #',
      accessor: 'invoNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 15.82,
    },
    {
      Header: 'Bic Date',
      accessor: 'invoDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.17,
    },
    {
      Header: 'Due Date',
      accessor: 'dueDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.17,
    },
    {
      Header: 'Bic Company',
      accessor: 'custName',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 22.61,
    },
  ]
};

export const infoAreaBicHistoryLineItemsTable:TableSettings = {
  tableVersion: 1002,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '160px',
  width: '710px',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    {
      Header: '#',
      accessor: 'seqNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 5.34,
    },
    {
      Header: 'Type',
      accessor: 'chgTypeDesc',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 25.38,
    },
    {
      Header: 'Billing Code',
      accessor: 'code',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 20.94,
    },
    {
      Header: 'Description',
      accessor: 'description',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 30.39,
    },
    {
      Header: 'CUR',
      accessor: 'currency',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.34,
    },
    {
      Header: 'Local Amount',
      accessor: 'amount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.61,
    },
  ]
};

export const infoAreaBicPaymentHistoryLineItemsTable:TableSettings = {
  tableVersion: 1003,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '160px',
  width: '695px',
  useStorage: true,
  flexResizer: true,

  headersColor: 'teal',
  columns: [
    {
      Header: '#',
      accessor: 'no',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 5.04,
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 13.67,
    },
    {
      Header: 'Check #/Ref #',
      accessor: 'checkNoRefNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 23.74,
    },
    {
      Header: 'P. Type',
      accessor: 'paymentType',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.79,
    },
    {
      Header: 'Accrual Date',
      accessor: 'accrualDate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 13.67,
    },
    {
      Header: 'CUR',
      accessor: 'cur',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.47,
    },
    {
      Header: 'Void',
      accessor: 'void',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 6.47,
    },
    {
      Header: 'Ex Rate',
      accessor: 'exRate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.79,
    },
    {
      Header: 'Draft',
      accessor: 'draft',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.36,
    },
  ]
};

export const infoAreaBicBlTable:TableSettings = {
  tableVersion: 1002,
  overflowX: 'hidden',
  overflowY: 'hidden',
  
  height: '160px',
  width: '1200px',
  useStorage: true,
  flexResizer: true,

  headersColor: 'teal',
  columns: [
    {
      Header: '#',
      accessor: 'no',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.35,
    },
    {
      Header: 'Type',
      accessor: 'type',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.75,
    },
    {
      Header: 'B/L #',
      accessor: 'blNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.83,
    },
    {
      Header: 'Ref. #',
      accessor: 'refNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.83,
    },
    {
      Header: 'D/ID',
      accessor: 'shipmentType',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 4.58,
    },
    {
      Header: 'Customer',
      accessor: 'customer',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.83,
    },
    {
      Header: 'Agent',
      accessor: 'agent',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.83,
    },
    {
      Header: 'ETD',
      accessor: 'etd',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.5,
    },
    {
      Header: 'ETA',
      accessor: 'eta',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.5,
    },
    {
      Header: 'F.ETA',
      accessor: 'feta',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.5,
    },
    {
      Header: 'PCS & Unit',
      accessor: 'pcsUnit',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.5,
    },
    {
      Header: 'KGS',
      accessor: 'kgs',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.5,
    },
    {
      Header: 'CBM',
      accessor: 'cbm',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 7.5,
    },
  ]
};

export const infoAreaBicRevisionHistoryLineItemsTable:TableSettings = {
  tableVersion: 1002,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '385px',
  width: '290px',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  columns: [
    {
      Header: 'Action',
      accessor: 'action',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 24.14,
    },
    {
      Header: 'Action Date',
      accessor: 'u2Date',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 31.03,
    },
    {
      Header: 'Bic #',
      accessor: 'invoNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 44.83,
    },
  ]
};

export const codeMiscMemoTitleList:TableSettings = {
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: false,

  headersColor: 'teal',
  alternateRows: true,
  columns: [
    {
      Header: '',
      accessor: 'indicator',
      resizable: false,
      sortable: false,
      canBeHidden: false,
      visible: true,
      width: 30,
      //hiddenInColumnSetup: true,
    },
    {
      Header: 'Title',
      accessor: 'description',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 200,
      filterable: true,
      filterType: 'text',
    },
    {
      Header: 'Inactive',
      accessor: 'inactive',
      resizable: true,
      sortable: true,
      canBeHidden: true,
      visible: true,
      width: 200,
      filterable: true,
      filterType: 'text',
    },
  ]
};

export const salespersonList:TableSettings = {
  tableVersion: 10048,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 6.38, },
    { accessor:'sManID', Header: 'Salesperson ID', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 31.91,},
    { accessor:'sManName', Header: 'Name', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 44.68, },
    { accessor:'inactive', Header: 'Inactive', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'bool',initialWidthPercentage: 12.77, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 4.26, },
  ]
};

export const departmentList:TableSettings = {
  tableVersion: 10047,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 7.32, },
    { accessor:'name', Header: 'Department', visible: true, resizable: false, sortable: true, initialWidthPercentage: 24.39, },
    { accessor:'description', Header: 'Description', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 48.78, },
    { accessor:'inactive', Header: 'Inactive', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'bool', initialWidthPercentage: 14.63, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 4.88, },
  ]
};

export const companyGroupList:TableSettings = {
  tableVersion: 10049,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 8.57, },
    { accessor:'groupId', Header: 'Group Name', visible: true, resizable: false, sortable: true, initialWidthPercentage: 28.57 },
    { accessor:'groupName', Header: 'Description', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 57.14, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, filterType: 'text',initialWidthPercentage: 5.72, },
  ]
};

export const branchList:TableSettings = {
  tableVersion: 10045,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 4.5, },
    { accessor:'branch', Header: 'Branch Code', visible: true, resizable: false, sortable: true, initialWidthPercentage: 10.5 },
    { accessor:'name', Header: 'Branch Name', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 15, },
    { accessor:'contact', Header: 'Contact', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 24, },
    { accessor:'state', Header: 'State', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 6.5, },
    { accessor:'phone', Header: 'Phone #', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 12, },
    { accessor:'fax', Header: 'Fax #', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 12, },
    { accessor:'hqBranch', Header: 'HQ', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'bool', initialWidthPercentage: 6.5, },
    { accessor:'inactive', Header: 'Inactive', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'bool', initialWidthPercentage: 6.5, },
    { accessor:'scroll', Header: '', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 2.5, },
  ]
};

export const billingTemplateList:TableSettings = {
  tableVersion: 10053,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 3.55, },
    { accessor:'templateName', Header: 'Name', visible: true, resizable: false, sortable: true, initialWidthPercentage: 9.9 },
    { accessor:'opType', Header: 'OP Type', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 16.86, },
    { accessor:'shipmentType', Header: 'Shipment', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 8.87, },
    { accessor:'department', Header: 'Department', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 8.87, },
    { accessor:'item', Header: 'Items', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'number', initialWidthPercentage: 5.06, },
    { accessor:'inactive', Header: 'Inactive', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'bool', initialWidthPercentage: 7.99, },
    { accessor:'inactiveDate', Header: 'Inactive Date', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'date', initialWidthPercentage: 10.94, },
    { accessor:'updateDate', Header: 'Update Date', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'date', initialWidthPercentage: 14.94, },
    { accessor:'updateBy', Header: 'Update By', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 10.94, },
    { accessor:'scroll', Header: '', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 2.08, },
  ]
};

export const billingTemplateEntryList:TableSettings = {
  tableVersion: 10067,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 5.52, },
    { accessor:'type', Header: 'Type', visible: true, resizable: false, sortable: false, initialWidthPercentage: 18.62 },
    { accessor:'billingCodeView', Header: 'Billing Code', visible: true, resizable: false, sortable: false, filterable: true, filterType: 'text', initialWidthPercentage: 26.66, },
    { accessor:'description', Header: 'Item Description', visible: true, resizable: false, sortable: false, filterable: true, filterType: 'text', initialWidthPercentage: 47.12, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, width: 20, },
  ]
};

export const airRateList:TableSettings = {
  tableVersion: 10043,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 4.17, },
    { accessor:'customer', Header: 'Customer', visible: true, resizable: false, sortable: true, initialWidthPercentage: 22.92 },
    { accessor:'carrier', Header: 'Carrier', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 22.92, },
    { accessor:'org', Header: 'Departure', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 10.42, },
    { accessor:'dest', Header: 'Arrival', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 10.42, },
    { accessor:'validFrom', Header: 'Valid From', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'date', initialWidthPercentage: 10.42, },
    { accessor:'validTo', Header: 'Valid To', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'date', initialWidthPercentage: 10.42, },
    { accessor:'expire', Header: 'Expire', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'bool', initialWidthPercentage: 6.25, },
    { accessor:'scroll', Header: '', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 2.06, },
  ]
};

export const oceanRateList:TableSettings = {
  tableVersion: 10043,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 4.17, },
    { accessor:'customer', Header: 'Customer', visible: true, resizable: false, sortable: true, initialWidthPercentage: 22.92 },
    { accessor:'commodity', Header: 'Commodity', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 22.92, },
    { accessor:'org', Header: 'Loading Port', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 10.42, },
    { accessor:'dest', Header: 'Discharge Port', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 10.42, },
    { accessor:'validFrom', Header: 'Valid From', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'date', initialWidthPercentage: 10.42, },
    { accessor:'validTo', Header: 'Valid To', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'date', initialWidthPercentage: 10.42, },
    { accessor:'expire', Header: 'Expire', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'bool', initialWidthPercentage: 6.25, },
    { accessor:'scroll', Header: '', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 2.06, },
  ]
};

export const sellingRateList:TableSettings = {
  tableVersion: 10028,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 2.2, },
    { accessor:'sName', Header: 'Customer', visible: true, resizable: false, sortable: true, initialWidthPercentage: 14.7 },
    { accessor:'mode', Header: 'Cargo', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 4.5, },
    { accessor:'expireETD', Header: 'Expire', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 6.2, },
    { accessor:'carrier', Header: 'Carrier', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 11 },
    { accessor:'origin', Header: 'Origin', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 11, },
    { accessor:'destination', Header: 'Destination', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 11, },
    { accessor:'commodity', Header: 'Commodity', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 13.9, },
    { accessor:'doorBy', Header: 'Door by', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 8, },
    { accessor:'size', Header: 'Size', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 4.5, },
    { accessor:'rate1', Header: 'Rate 1', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 5.9, },
    { accessor:'rate2', Header: 'Rate 2', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 5.9, },
    { accessor:'remark', Header: 'Remark', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 7.55, },
    { accessor:'scroll', Header: '', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 3, },
  ]
};

export const oceanRateInfoList:TableSettings = {
  tableVersion: 10044,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 4.92, },
    { accessor:'mode', Header: 'Mode', visible: true, resizable: false, sortable: true, initialWidthPercentage: 8.20 },
    { accessor:'tTime', Header: 'Transit Time', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 13.11, },
    { accessor:'carrier', Header: 'Carrier', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 13.11, },
    { accessor:'desc', Header: 'Description of Freight Charge', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 29.51, },
    { accessor:'unit', Header: 'Unit', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 9.84, },
    { accessor:'cur', Header: 'CUR', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 6.56, },
    { accessor:'bRate', Header: 'Buying Rate', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'number', initialWidthPercentage: 11.48, },
    { accessor:'scroll', Header: '', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 3.27, },
  ]
};

export const airRatePartyInfoList:TableSettings = {
  overflowX: 'hidden',
  overflowY: 'hidden',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 22.72, },
    { accessor:'col1', Header: '0~45', visible: true, resizable: false, sortable: false, initialWidthPercentage: 12.88, },
    { accessor:'col2', Header: '46~100', visible: true, resizable: false, sortable: false, initialWidthPercentage: 12.88, },
    { accessor:'col3', Header: '101~300', visible: true, resizable: false, sortable: false, initialWidthPercentage: 12.88, },
    { accessor:'col4', Header: '301~500', visible: true, resizable: false, sortable: false, initialWidthPercentage: 12.88, },
    { accessor:'col5', Header: '501~1000', visible: true, resizable: false, sortable: false, initialWidthPercentage: 12.88, },
    { accessor:'col6', Header: 'Over 1000', visible: true, resizable: false, sortable: false, initialWidthPercentage: 12.88, },
  ]
};

export const sellingRateInfoList:TableSettings = {
  tableVersion: 10000,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 100, },
  ]
};


export const securityGroupList:TableSettings = {
  tableVersion: 1002,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 5.16, },
    { accessor:'groupName', Header: 'Group Name', visible: true, resizable: false, sortable: false, initialWidthPercentage: 23.23, },
    { accessor:'description', Header: 'Description', visible: true, resizable: false, sortable: false, initialWidthPercentage: 32.26, },
    { accessor:'inactive', Header: 'Inactive', visible: true, resizable: false, sortable: false, initialWidthPercentage: 9.67, },
    { accessor:'inactiveDate', Header: 'Inactive Date', visible: true, resizable: false, sortable: false, initialWidthPercentage: 14.84, },
    { accessor:'inactiveBy', Header: 'By', visible: true, resizable: false, sortable: false, initialWidthPercentage: 14.84, },
  ]
};

export const securityAssignmentList:TableSettings = {
  tableVersion: 10042,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 6.82, },
    { accessor:'taskDesc', Header: 'Security Task Name', visible: true, resizable: false, sortable: false, initialWidthPercentage: 34.09, },
    { accessor:'attrRead', Header: 'Read', visible: true, resizable: true, sortable: false, initialWidthPercentage: 13.64, },
    { accessor:'attrAdd', Header: 'Add', visible: true, resizable: true, sortable: false, initialWidthPercentage: 13.64, },
    { accessor:'attrEdit', Header: 'Modify', visible: true, resizable: true, sortable: false, initialWidthPercentage: 13.64, },
    { accessor:'attrDel', Header: 'Delete', visible: true, resizable: true, sortable: false, initialWidthPercentage: 13.64, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 4.53, },
  ]
};

export const securityGroupUserList:TableSettings = {
  tableVersion: 10041,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  alternateRows: true,
  columns: [
    { accessor:'userId', Header: 'User Id', visible: true, resizable: false, sortable: false, initialWidthPercentage: 93.10, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 6.90, },
  ]
};

export const securityControlList:TableSettings = {
  tableVersion: 10040,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'desc', Header: 'Name', visible: true, resizable: false, sortable: false, initialWidthPercentage: 92.59, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 7.41, },
  ]
};

export const securityStateList:TableSettings = {
  tableVersion: 10040,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'controlDesc', Header: 'Name', visible: true, resizable: false, sortable: false, initialWidthPercentage: 92.59, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 7.41, },
  ]
};

export const userSecurityList:TableSettings = {
  tableVersion: 1002,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 2.65, },
    { accessor:'userId', Header: 'User Id', visible: true, resizable: false, sortable: false, filterType: 'text', initialWidthPercentage: 11.92, },
    { accessor:'name', Header: 'Name', visible: true, resizable: false, sortable: false, filterType: 'text', initialWidthPercentage: 11.26, },
    { accessor:'admin', Header: 'Admin', visible: true, resizable: false, sortable: false, filterType: 'bool', initialWidthPercentage: 9.07, },
    { accessor:'acct', Header: 'ACCT', visible: true, resizable: false, sortable: false, filterType: 'bool', initialWidthPercentage: 9.07, },
    { accessor:'acctm', Header: 'ACCT-M', visible: true, resizable: false, sortable: false, filterType: 'bool', initialWidthPercentage: 9.07, },
    { accessor:'op', Header: 'O/P', visible: true, resizable: false, sortable: false, filterType: 'bool', initialWidthPercentage: 9.07, },
    { accessor:'opm', Header: 'O/P-M', visible: true, resizable: false, sortable: false, filterType: 'bool', initialWidthPercentage: 9.07, },
    { accessor:'sales', Header: 'Sales', visible: true, resizable: false, sortable: false, filterType: 'bool', initialWidthPercentage: 9.07, },
    { accessor:'salesm', Header: 'Sales M', visible: true, resizable: false, sortable: false, filterType: 'bool', initialWidthPercentage: 9.07, },
    { accessor:'controls', Header: ' ', visible: true, resizable: false, sortable: false, initialWidthPercentage: 10.68, },
  ]
};

export const userSecurityList2:TableSettings = {
  tableVersion: 10006,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: false,    // **
  flexResizer: true,

  headersColor: 'white',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 4, },
    { accessor:'userId', Header: 'User Id', visible: true, resizable: true, sortable: true, filterType: 'text', initialWidthPercentage: 10, },
    { accessor:'userName', Header: 'Name', visible: true, resizable: true, sortable: true, filterType: 'text', initialWidthPercentage: 16, },
  ]
};

export const bankList:TableSettings = {
  tableVersion: 10046,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 3.23, },
    { accessor:'bankName', Header: 'Bank Short Name', visible: true, resizable: true, sortable:false, filterType:'text', initialWidthPercentage: 14.52, },
    { accessor:'glNo', Header: 'Account Type', visible: true, resizable: true, sortable: false, filterType:'text', initialWidthPercentage: 12.10, },
    { accessor:'currency', Header: 'CUR', visible: true, resizable: true, sortable: false, filterType:'text', initialWidthPercentage: 4.84, },
    { accessor:'bankAcc', Header: 'Account #', visible: true, resizable: true, sortable: false, filterType:'text', initialWidthPercentage: 12.10, },
    { accessor:'nextCheckNo', Header: 'Next Check #', visible: true, resizable: true, sortable: false, filterType:'number', initialWidthPercentage: 8.06, },
    { accessor:'endCheckNo', Header: 'End Check #', visible: true, resizable: true, sortable: false, filterType:'number', initialWidthPercentage: 8.06, },
    { accessor:'branch', Header: 'BR', visible: true, resizable: true, sortable: false, filterType:'text', initialWidthPercentage: 4.84, },
    { accessor:'defaultBank', Header: 'Default', visible: true, resizable: true, sortable: false, filterType:'bool', initialWidthPercentage: 4.84, },
    { accessor:'inact', Header: 'Inactive', visible: true, resizable: true, sortable: false, filterType:'bool', initialWidthPercentage: 4.84, },
    { accessor:'inactDate', Header: 'Inactive Date', visible: true, resizable: true, sortable: false, filterType:'date', initialWidthPercentage: 6.45, },
    { accessor:'updateDate', Header: 'Update Date', visible: true, resizable: true, sortable: false, filterType:'date', initialWidthPercentage: 8.06, },
    { accessor:'updateBy', Header: 'By', visible: true, resizable: true, sortable: false, filterType:'text', initialWidthPercentage: 6.45, },
    { accessor:'scroll', Header: '', visible: true, resizable: true, sortable: false, initialWidthPercentage: 1.61, },
  ]
};


export const creditCardList:TableSettings = {
  tableVersion: 1008,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 2.73, },
    { accessor:'acctSName', Header: 'Credit Card Name', visible: true, resizable: true, sortable: false, filterType: 'text',  initialWidthPercentage: 13.64, },
    { accessor:'cardType', Header: 'Card Type', visible: true, resizable: true, sortable: false,  filterType: 'text', initialWidthPercentage: 7.27, },
    { accessor:'cardNumber', Header: 'Credit Card #', visible: true, resizable: true, sortable: false,  filterType: 'text', initialWidthPercentage: 9.09, },
    { accessor:'expiry', Header: 'Expire', visible: true, resizable: true, sortable: false, filterType: 'text', initialWidthPercentage: 7.27, },
    { accessor:'paymentDate', Header: 'Due Date', visible: true, resizable: true, sortable: false,  filterType: 'number', initialWidthPercentage: 5.45, },
    { accessor:'sName', Header: 'Credit Card Company', visible: true, resizable: true, sortable: false,  filterType: 'text', initialWidthPercentage: 10.91, },
    { accessor:'nameOnCard', Header: 'Name on Card', visible: true, resizable: true, sortable: false,  filterType: 'text',  initialWidthPercentage: 10.91, },
    { accessor:'inactive', Header: 'Inactive', visible: true, resizable: true, sortable: false,  filterType: 'bool', initialWidthPercentage: 5.45, },
    { accessor:'inactiveDate', Header: 'Inactive Date', visible: true, resizable: true, sortable: false,  filterType: 'date', initialWidthPercentage: 7.27, },
    { accessor:'updateDate', Header: 'Update Date', visible: true, resizable: true, sortable: false, filterType: 'date',  initialWidthPercentage: 10.91, },
    { accessor:'updateBy', Header: 'By', visible: true, resizable: true, sortable: false, filterType: 'text', initialWidthPercentage: 7.27, },
    { accessor:'scroll', Header: '', visible: true, resizable: true, sortable: false, initialWidthPercentage: 1.83, },
  ]
};

export const payrollExpenseList:TableSettings = {
  tableVersion: 10061,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: false,
  flexResizer: false,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, width: 30, },
    { accessor:'department', Header: 'Department', visible: true, resizable: true, sortable: false, width: 100, },
    { accessor:'branch', Header: 'BR', visible: true, resizable: true, sortable: false, width: 60, },
    { accessor:'acD4Salary', Header: 'Salaries & Wages Expense', visible: true, resizable: true, sortable: false, width: 200, },
  ]
};

export const payrollAdditionalItemsList:TableSettings = {
  tableVersion: 10058,
  overflowX: 'hidden',
  overflowY: 'hidden',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 4.23, },
    { accessor:'select', Header: 'Select', visible: true, resizable: false, sortable: false, initialWidthPercentage: 8.45, },
    { accessor:'payable', Header: 'Item Title', visible: true, resizable: true, sortable: false, initialWidthPercentage: 28.17, },
    { accessor:'accountCode', Header: 'Account Code', visible: true, resizable: true, sortable: false, initialWidthPercentage: 30.98, },
    { accessor:'entityShortName', Header: 'Entity', visible: true, resizable: false, sortable: false, initialWidthPercentage: 28.17, },
  ]
};

export const payrollGroupList :TableSettings = {
  tableVersion: 10011,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: false,
  flexResizer: false,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, width: 30, },
    { accessor:'groupName', Header: 'Name', visible: true, resizable: true, sortable: false, width: 140, },
    { accessor:'glNo4SalaryCode', Header: 'Wage Expense Account Code', visible: true, resizable: true, sortable: false, width: 300, },
  ]
};

export const payrollEntityList :TableSettings = {
  tableVersion: 10010,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: false,
  flexResizer: false,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, width: 30, },
    { accessor:'billingCode', Header: 'Item', visible: true, resizable: true, sortable: false, width: 200, },
    { accessor:'entityName', Header: 'Entity', visible: true, resizable: true, sortable: false, width: 220, },
    { accessor:'glNoExp', Header: 'Expense Account Code', visible: true, resizable: true, sortable: false, width: 330, },
  ]
};


export const accountCodeList:TableSettings = {
  tableVersion: 10050,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 3.17, },
    { accessor:'accountCode', Header: 'Account Code', visible: true, resizable: true, sortable: true, filterable: true, initialWidthPercentage: 6.35, },
    { accessor:'accountName', Header: 'Default Account Name', visible: true, resizable: true, sortable: true, filterable: true, initialWidthPercentage: 15.87, },
    { accessor:'glType', Header: 'Group', visible: true, resizable: true, sortable: true, filterable: true, initialWidthPercentage: 6.35, },
    { accessor:'subGroup1', Header: 'Sub Group 1', visible: true, resizable: true, sortable: true, filterable: true, initialWidthPercentage: 11.90, },
    { accessor:'subGroup2', Header: 'Sub Group 2', visible: true, resizable: true, sortable: true, filterable: true, initialWidthPercentage: 11.90, },
    { accessor:'iocalDesc', Header: 'Custom Account Name', visible: true, resizable: true, sortable: true, filterable: true, initialWidthPercentage: 15.87, },
    { accessor:'inactive', Header: 'Inactive', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'bool', filterTypeV2: XCellType.Bool, initialWidthPercentage: 4.76, },
    { accessor:'inactiveDate', Header: 'Inactive Date', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'date', filterTypeV2: XCellType.Date, initialWidthPercentage: 6.35 },
    { accessor:'remark', Header: 'Remark', visible: true, resizable: true, sortable: true, filterable: true, initialWidthPercentage: 15.87, },
    { accessor:'scroll', Header: '', visible: true, resizable: true, sortable: false, initialWidthPercentage: 1.61, },
  ]
};

export const accountSubCodeList:TableSettings = {
  tableVersion: 10042,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 8.57, },
    { accessor:'code', Header: 'Account Sub Code', visible: true, resizable: true, sortable: false,initialWidthPercentage: 28.57, },
    { accessor:'desc1', Header: 'Description', visible: true, resizable: true, sortable: false, initialWidthPercentage: 57.14, },
    { accessor:'scroll', Header: '', visible: true, resizable: true, sortable: false, initialWidthPercentage: 5.72, },
  ]
};

export const billingCodeList:TableSettings = {
  tableVersion: 10052,
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false, width: 40, },
    { accessor:'code', Header: 'Billing Code', visible: true, resizable: false, sortable: true, filterable: true, width: 190, },
    { accessor:'desc1', Header: 'Default Description', visible: true, resizable: true, sortable: false, filterable: true, width: 210, },
    { accessor:'jobType', Header: 'Job Type', visible: true, resizable: true, sortable: false, filterable: true, width: 120, },
    { accessor:'localDesc', Header: 'Custom Description', visible: true, resizable: true, sortable: false, filterable: true, width: 210, },
    { accessor:'salesGLNo', Header: 'Sales Acct.Code', visible: true, resizable: true, sortable: true, filterable: true, width: 90, },
    { accessor:'salesGLNoDesc', Header: 'Sales Acct.Code Desc.', visible: true, resizable: true, sortable: false, filterable: true, width: 190, },
    { accessor:'costGLNo', Header: 'Cost Acct.Code', visible: true, resizable: true, sortable: true, filterable: true, width: 90, },
    { accessor:'costGLNoDesc', Header: 'Cost Acct.Code Desc.', visible: true, resizable: true, sortable: false, filterable: true, width: 190, },
    { accessor:'inactive', Header: 'Inactive', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'bool', filterTypeV2: XCellType.Bool, width: 60, },
    { accessor:'inactiveDate', Header: 'Inactive Date', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'date', filterTypeV2: XCellType.Date, width: 80, },
    { accessor:'remark', Header: 'Remark', visible: true, resizable: true, sortable: false, filterable: true, width: 200, },
    { accessor:'scroll', Header: '', visible: true, resizable: true, sortable: false, width: 20, },
  ]
};

export const billingCodeDetailList:TableSettings = {
  tableVersion: 1004,
  overflowX: 'hidden',
  overflowY: 'hidden',
  height: '100%',
  useStorage: true,
  flexResizer: true,
  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'opType', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 7.5, },
    { accessor:'shipmentType', Header: 'Type', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 7.5, },
    { accessor:'sCode', Header: 'Sales Code', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 15, },
    { accessor:'sCodeName', Header: 'S. Code Name', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 27.5, },
    { accessor:'cCode', Header: 'Cost Code', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 15, },
    { accessor:'cCodeName', Header: 'C. Code Name', visible: true, resizable: true, sortable: false, filterable: true, initialWidthPercentage: 27.5, }
  ]
};


export const editEventList:TableSettings = {
  tableVersion: 10047,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 9.52, },
    { accessor:'isUse', Header: 'Use', visible: true, resizable: false, sortable: false, initialWidthPercentage: 19.05, },
    { accessor:'event', Header: 'Event', visible: true, resizable: false, sortable: false, initialWidthPercentage: 66.83, },
    { accessor: 'scroll', Header:'', visible:true, resizable:false, sortable:false, initialWidthPercentage :4.60, },
 ]
};

export const edit301List:TableSettings = {
  tableVersion: 10049,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 9.52, },
    { accessor:'ediCode', Header: 'EDI Code', visible: true, resizable: true, sortable: false, initialWidthPercentage: 28.57, },
    { accessor:'importChargeCode', Header: 'FS Code (OI)', visible: true, resizable: true, sortable: false, initialWidthPercentage: 28.57, },
    { accessor:'exportChargeCode', Header: 'FS Code (OE)', visible: true, resizable: true, sortable: false, initialWidthPercentage: 28.57, },
    { accessor: 'scroll', Header: '', resizable: false, sortable: false, visible: true, initialWidthPercentage: 4.77, },
 ]
};

export const edit310List:TableSettings = {
  tableVersion: 10047,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 9.52, },
    { accessor:'ediCode', Header: 'EDI Code', visible: true, resizable: true, sortable: false, initialWidthPercentage: 42.86, },
    { accessor:'chargeCode', Header: 'FS Code', visible: true, resizable: true, sortable: false, initialWidthPercentage: 42.86, },
    { accessor: 'scroll', Header: '', resizable: false, sortable: false, visible: true, initialWidthPercentage: 4.76, },
 ]
};

export const serverBranchList:TableSettings = {
  tableVersion: 10050,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: false,
  flexResizer: true,

  headersColor: 'white',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 5, },
    { accessor:'branchDB', Header: 'Branch DB', visible: true, resizable: true, sortable: false, initialWidthPercentage: 10, },
    { accessor:'dbName', Header: 'Branch DB Name', visible: true, resizable: true, sortable: false, initialWidthPercentage: 25, },
    { accessor:'location', Header: 'Location', visible: true, resizable: true, sortable: false, initialWidthPercentage: 25, },
    { accessor:'dbId', Header: 'ID', visible: true, resizable: false, sortable: true, initialWidthPercentage: 15, },
    { accessor:'dbPassword', Header: 'Password', visible: true, resizable: true, sortable: false, initialWidthPercentage: 17, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 1, },
 ]
};

export const codesAirlineList:TableSettings = {
  tableVersion: 10050,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 3.24, },
    { accessor:'name', Header: 'Airline Name', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 15.05,},
    { accessor:'country', Header: 'Country', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', initialWidthPercentage: 6.47, },
    { accessor:'prefix', Header: 'Prefix', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text',initialWidthPercentage: 6.47, },
    { accessor:'abbr', Header: 'Abbr 2', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text',initialWidthPercentage: 6.47, },
    { accessor:'abbr3', Header: 'Abbr 3', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text',initialWidthPercentage: 6.47, },
    { accessor:'nextblNo', Header: 'Next AWB #', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text',initialWidthPercentage: 8.09, },
    { accessor:'endblNo', Header: 'End AWB #', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text',initialWidthPercentage: 8.09, },
    { accessor:'billTo', Header: 'Freight Vendor', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text',initialWidthPercentage: 12.14, },
    { accessor:'aefscRate', Header: 'FSC Rate', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'number',initialWidthPercentage: 6.47 },
    { accessor:'aefscMin', Header: 'FSC Min', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'number',initialWidthPercentage: 6.47 },
    { accessor:'aesscRate', Header: 'SSC Rate', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'number',initialWidthPercentage: 6.47 },
    { accessor:'aesscMin', Header: 'SSC Min', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'number',initialWidthPercentage: 6.47 },
    // { accessor:'updatedDate', Header: 'Update Date', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'date',initialWidthPercentage: 8.09 },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 1.62, },
  ]
};

export const codesAirportList:TableSettings = {
  tableVersion: 10044,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 9.26, },
    { accessor:'portName', Header: 'Airport Name', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 38.53,},
    { accessor:'code', Header: 'Code', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', initialWidthPercentage: 24.69, },
    { accessor:'usCode', Header: 'Custom Code', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text',initialWidthPercentage: 24.69, },
   // { accessor:'action', Header: 'Action', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 18.52, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 4.63, },
  ]
};

export const codesObltypeList:TableSettings = {
  tableVersion: 10043,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 9.26, },
    { accessor:'mblType', Header: 'Original B/L Type', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 86.11,},
    // { accessor:'action', Header: 'Action', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 18.52, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 4.63, },
  ]
};


export const codesContainerTypeList:TableSettings = {
  tableVersion: 10046,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor: 'indicator', Header: '', resizable: false, sortable: false, visible: true, initialWidthPercentage: 7.26 },
    { accessor: 'containerType', Header: 'Container Type', resizable: true, sortable: false, visible: true, initialWidthPercentage: 23.14 },
    { accessor: 'description', Header: 'Description', resizable: true, sortable: false, visible: true, initialWidthPercentage: 36.19 },
    { accessor: 'containerGroup', Header: 'Group', resizable: true, sortable: false, visible: true, initialWidthPercentage: 14.89 },
    { accessor: 'isoType', Header: 'ISO Type', resizable: true, sortable: false, visible: true, initialWidthPercentage: 14.89 },
    // { accessor: 'length', Header: 'Length', resizable: true, sortable: true, visible: true, initialWidthPercentage: 6.47 },
    // { accessor: 'height', Header: 'Height', resizable: true, sortable: true, visible: true, initialWidthPercentage: 6.47 },
    // { accessor: 'relatedType', Header: 'Type', resizable: true, sortable: true, visible: true, initialWidthPercentage: 6.47 },
    // { accessor: 'inactive', Header: 'Inactive', resizable: true, sortable: true, visible: true, initialWidthPercentage: 6.47 },
    // { accessor: 'inactiveDate', Header: 'Inactive Date', resizable: true, sortable: true, visible: true, initialWidthPercentage: 8.09 },
    // { accessor: 'u2Date', Header: 'Update Date', resizable: true, sortable: true, visible: true, initialWidthPercentage: 8.90 },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 2.31, },
  ]
};

export const codesMoveTypeList:TableSettings = {
  tableVersion: 10043,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor: 'indicator', Header: '', resizable: false, sortable: false, visible: true, initialWidthPercentage: 9.26, },
    { accessor: 'moveType', Header: 'Type', resizable: true, sortable: false, visible: true, initialWidthPercentage: 23.15 },
    { accessor: 'description', Header: 'Description', resizable: true, sortable: false, visible: true, initialWidthPercentage: 62.96 },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 4.63, },
  ]
};


export const codesPortCodeList:TableSettings = {
  tableVersion: 10045,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor: 'indicator', Header: '', resizable: false, sortable: false, visible: true, initialWidthPercentage: 4.63 },
    { accessor: 'code', Header: 'Code', resizable: true, sortable: false, visible: true,filterable: true, filterType: 'text', initialWidthPercentage: 11.57 },
    { accessor: 'portName', Header: 'Port Name', resizable: true, sortable: false, visible: true,filterable: true, filterType: 'text', initialWidthPercentage: 40.58 },
    { accessor: 'cyName', Header: 'CY Location', resizable: true, sortable: false, visible: true, filterable: true, filterType: 'text',initialWidthPercentage: 20.45 },
    { accessor: 'cfsName', Header: 'CFS Location', resizable: true, sortable: false, visible: true, filterable: true, filterType: 'text', initialWidthPercentage: 20.45},
   // { accessor: 'action', Header: 'Action', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 9.26, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: false, initialWidthPercentage: 2.31, },
  ]
};


export const codesVesselNameList: TableSettings = {
    tableVersion: 10044,
    overflowX: 'hidden',
    overflowY: 'scroll',
    
    height: '100%',
    useStorage: true,
    flexResizer: true,
  
    headersColor: 'gray',
    alternateRows: true,
    columns: [
        { accessor: 'indicator', Header: '', resizable: false, sortable: false, visible: true, initialWidthPercentage: 4.63 },
        { accessor: 'vesselName', Header: 'Vessel Name', resizable: true, sortable: false, visible: true, filterable: true, filterType: 'text', initialWidthPercentage: 46.76 },
        { accessor: 'country', Header: 'Country', resizable: true, sortable: false, visible: true, filterable: true, filterType: 'text', initialWidthPercentage: 11.57 },
        { accessor: 'vesselFlag', Header: 'Vessel Flag', resizable: true, sortable: false, visible: true, filterable: true, filterType: 'text', initialWidthPercentage: 11.57 },
        { accessor: 'lloydsCode', Header: 'Lloyds', resizable: true, sortable: false, visible: true, filterable: true, filterType: 'text', initialWidthPercentage: 11.58 },
        { accessor: 'groupName', Header: 'Group', resizable: true, sortable: false, visible: true, filterable: true, filterType: 'text', initialWidthPercentage: 11.58 },
        // { accessor: 'action', Header: 'Action', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 9.26, },
        { accessor: 'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 2.31, },
    ]
};

export const codesOtherShipmentTypeList:TableSettings = {
  tableVersion: 10046,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 4.63, },
    { accessor:'type', Header: 'Type', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 23.15,},
    { accessor:'description', Header: 'Description', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 46.76,},
    { accessor:'prefix', Header: 'Prefix', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 11.57,},
    { accessor:'no', Header: 'Number', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 11.57,},
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 2.31, },
  ]
};


export const codesAesExportInfoList:TableSettings = {
  tableVersion: 10044,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 4.63, },
    { accessor:'code', Header: 'Export Info', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 11.57,},
    { accessor:'description', Header: 'Description', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 81.46,},
   // { accessor:'action', Header: 'Action', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 9.26, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 2.31, },
  ]
};


export const codesAesExportLicenseList:TableSettings = {
  tableVersion: 10044,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 4.63, },
    { accessor:'code', Header: 'License Info', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 16.20,},
    { accessor:'description', Header: 'Description', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 76.85,},
  //  { accessor:'action', Header: 'Action', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 9.26, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 2.32, },
  ]
};

export const codesAesScheduleBList:TableSettings = {
  tableVersion: 10043,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 4.63, },
    { accessor:'code', Header: 'Schedule B', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 13.89,},
    { accessor:'sdescription', Header: 'Description', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 56.02,},
    { accessor:'unit1', Header: 'unit1', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 11.57,},
    { accessor:'unit2', Header: 'unit2', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 11.57,},
   // { accessor:'action', Header: 'Action', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 9.26, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 2.31, },
  ]
};

export const codesAesCarrierList:TableSettings = {
  tableVersion: 10045,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 4.63, },
    { accessor:'carrierName', Header: 'Carrier Name', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 31.71,},
    { accessor:'code', Header: 'Code', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 9.26,},
    { accessor:'sName', Header: 'Company', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 17.36,},
    { accessor:'senderId', Header: 'Sender Id', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 11.57,},
    { accessor:'receiverId', Header: 'Receiver Id', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 11.57,},
    { accessor:'customerCode', Header: 'Customer Code', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text',initialWidthPercentage: 11.57, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 2.31, },
  ]
};

export const codesCurrencyList:TableSettings = {
  tableVersion: 10054,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false,  initialWidthPercentage: 9.26, },
    { accessor:'code', Header: 'Currency', visible: true, resizable: false, sortable: false, filterable: true, filterType: 'text', initialWidthPercentage: 18.30 },
    { accessor:'decimal', Header: 'Decimal Digit', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'number', initialWidthPercentage: 20.15,formatString: '#,##0' },
    { accessor:'symbol', Header: 'Symbol', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', initialWidthPercentage: 15.52, },
    { accessor:'localSymbol', Header: 'Local Symbol', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', initialWidthPercentage: 20.15, },
    { accessor:'inactive', Header: 'Inactive', visible: true, resizable: true, sortable: false, filterable: true, filterTypeV2: XCellType.Bool, initialWidthPercentage: 12.00, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 4.63, },
  ]
};

export const codesExchangeRateList:TableSettings = {
  tableVersion: 10045,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 5.41, },
    { accessor:'exchageDate', Header: 'Date', visible: true, resizable: false, sortable: false, filterable: true, filterType: 'date',initialWidthPercentage: 12.36 },
    { accessor:'currency', Header: 'CUR', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', initialWidthPercentage: 11.65, },
    { accessor:'rate', Header: 'Rate', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'number', initialWidthPercentage: 12.36, formatString: '#,##0.0000' },
    { accessor:'createDate', Header: 'Create Date', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'date', initialWidthPercentage: 15.06, },
    { accessor:'createBy', Header: 'Create By', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', initialWidthPercentage: 12.35, },
    { accessor:'updateDate', Header: 'Update Date', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'date', initialWidthPercentage: 15.05 },
    { accessor:'updateBy', Header: 'Update By', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', initialWidthPercentage: 12.35, },
  //  { accessor: 'action', Header: 'Action', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 10.81, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 2.70, },
  ]
};

export const codesRegionCountryList:TableSettings = {
  tableVersion: 10045,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 9.26, },
    { accessor:'isoCode', Header: 'ISO Code', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 24.69,},
    { accessor:'countryName', Header: 'Country', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 41.37,},
    { accessor:'usCode', Header: 'US Code', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 20.05},
   // { accessor:'action', Header: 'Action', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 18.52, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 4.63, },
  ]
};

export const codesRegionStateList: TableSettings = {
    tableVersion: 10046,
    overflowX: 'hidden',
    overflowY: 'scroll',
    
    height: '100%',
    useStorage: true,
    flexResizer: true,
  
    headersColor: 'gray',
    alternateRows: true,
    columns: [
        { accessor: 'indicator', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 9.26, },
        { accessor: 'code', Header: 'Code', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 24.35, },
        { accessor: 'stateName', Header: 'State', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 41.56, },
        { accessor: 'country', Header: 'Country', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 20.20, },
        //{ accessor:'action', Header: 'Action', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 18.52, },
        { accessor: 'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 4.63, },
    ]
};

export const codesAnTitleList:TableSettings = {
  tableVersion: 10047,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 4.63, },
    { accessor:'antitle', Header: 'Arrival Notice Title', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 66.54,},
    { accessor:'antitle1', Header: 'Show Charge Menu', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 13.26,},
    { accessor:'antitle2', Header: 'Change A/N Form', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 13.26,},
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 2.31, },
  ]
};

export const codesCommodityList:TableSettings = {
  tableVersion: 10043,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 4.63, },
    { accessor:'code', Header: 'Commodity', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 30.09,},
    { accessor:'description', Header: 'Description', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 62.96,},
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 2.31, },
  ]
};

export const codesEdi315List:TableSettings = {
  tableVersion: 10043,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor: 'indicator', Header: '', resizable: false, sortable: false, visible: true, initialWidthPercentage: 5.88, },
    { accessor: 'description', Header: 'Event Description', resizable: true, sortable: false, visible: true, initialWidthPercentage: 52.94 },
    { accessor: 'status', Header: 'Status', resizable: true, sortable: false, visible: true, initialWidthPercentage: 14.7 },
    { accessor: 'equipment', Header: 'Equip', resizable: true, sortable: false, visible: true, initialWidthPercentage: 11.76 },
    { accessor: 'ediOrder', Header: 'Order', resizable: true, sortable: false, visible: true, initialWidthPercentage: 11.76 },
   // { accessor: 'action', Header: 'Action', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 11.76, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 2.94, },
  ]
};

export const codesMemoTitleList:TableSettings = {
  tableVersion: 10042,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor: 'indicator', Header: '', resizable: false, sortable: false, visible: true, initialWidthPercentage: 9.26, },
    { accessor: 'description', Header: 'Event Description', resizable: true, sortable: false, visible: true, initialWidthPercentage: 86.11 },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 4.63, },
  ]
};

export const codesMemoReminderTaskList:TableSettings = {
  tableVersion: 10046,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 3.24, },
    { accessor:'task', Header: 'Task', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 4.85,},
    { accessor:'asigned', Header: 'Assigned', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 6.47,},
    { accessor:'remindDate', Header: 'Remind Date', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 6.47,},
    { accessor:'dueDate', Header: 'Due Date', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 6.47,},
    { accessor:'pending', Header: 'Pending', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 6.47,},
    { accessor:'createdAt', Header: 'Create', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 6.47, },
    { accessor:'createdBy', Header: 'Create By', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 6.47, },
    { accessor:'referenceNo', Header: 'Ref. #', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 9.39, },
    { accessor:'title', Header: 'Title', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 16.18, },
    { accessor:'contents', Header: 'Contents', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 34.36, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 2.31, },
  ]
};

export const codesMemoAttachedFileList:TableSettings = {
  tableVersion: 10043,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 3.24, },
    { accessor:'userId', Header: 'User ID', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 8.09,},
    { accessor:'date', Header: 'Date', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 6.47,},
    { accessor:'refNo', Header: 'Ref. #', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 8.09,},
    { accessor:'title', Header: 'Title', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 16.18,},
    { accessor:'fileName', Header: 'Attached File Name', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 16.18,},
    { accessor:'fileSize', Header: 'File Size', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 6.47, },
    { accessor:'contents', Header: 'Contents', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 33.66, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 1.62, },
  ]
};

export const codesRegionUnLocationCodeList:TableSettings = {
  tableVersion: 1004,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 5.88, },
    { accessor:'unCode', Header: 'UN Code', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 14.70,},
    { accessor:'unPortName', Header: 'Port Name', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 47.06,},
    { accessor:'unCountry', Header: 'Country', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 14.70,},
    { accessor:'usCode', Header: 'US Code', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 14.70,},
    //{ accessor:'action', Header: 'Action', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 11.76, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 2.94, },
  ]
};

export const codesLogFileEmailLogList:TableSettings = {
  tableVersion: 10045,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 3.24, },
    { accessor:'senderId', Header: 'User Name', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 8.09,},
    { accessor:'title', Header: 'Title', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 16.18,},
    { accessor:'recipient', Header: 'Recipient', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 20.23,},
    { accessor:'recCompanyName', Header: 'Company Name', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 12.62,},
    { accessor:'sType', Header: 'Type', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 4.85,},
    { accessor:'recNo', Header: 'Address', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 20.23,},
    { accessor:'state', Header: 'Status', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 4.85,},
    { accessor:'postDate', Header: 'Date', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 8.09,},
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 2.31, },
  ]
};

export const codesLogFilePrintLogList:TableSettings = {
  tableVersion: 10045,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 3.24, },
    { accessor:'senderId', Header: 'User Id', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 11.57,},
    { accessor:'title', Header: 'Title', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 69.91,},
    { accessor:'postDate', Header: 'Date', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 11.57,},
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 2.31, },
  ]
};

export const codesLogFileFaxLogList:TableSettings = {
  tableVersion: 10045,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 3.24, },
    { accessor:'postDate', Header: 'Date', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 11.57,},
    { accessor:'senderId', Header: 'User Name', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 11.57,},
    { accessor:'recCompanyName', Header: 'Company Name', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 17.36,},
    { accessor:'recipient', Header: 'Recipient', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 11.57,},
    { accessor:'state', Header: 'Status', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 6.94,},
    { accessor:'recNo', Header: 'Fax #', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 9.26,},
    { accessor:'title', Header: 'Title', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', defaultColumnFocus: true, initialWidthPercentage: 24.77,},
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, filterable: true, initialWidthPercentage: 2.31, },
  ]
};

export const billOrInvoiceSummaryLineItems:TableSettings = {
  tableVersion: 1012,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '200px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',

  columns: [
    { Header: 'Type', accessor: 'type', resizable: true, sortable: false, visible: true, initialWidthPercentage: 3, }, 
    { Header: '#', accessor: 'no', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.95, }, 
    { Header: 'Date', accessor: 'date', resizable: true, sortable: false, visible: true, initialWidthPercentage: 5.30, },
    { Header: 'Entity', accessor: 'entity', resizable: true, sortable: false, visible: true, initialWidthPercentage: 10.24, },
    { Header: 'Billing Code', accessor: 'billingCode', resizable: true, sortable: false, visible: true, initialWidthPercentage: 9.93, },
    { Header: 'Item Description', accessor: 'itemDescription', resizable: true, sortable: false, visible: true, initialWidthPercentage: 9.93, },
    { Header: 'Draft', accessor: 'draft', resizable: true, sortable: false, visible: true, initialWidthPercentage: 3.31, },
    { Header: 'CUR', accessor: 'cur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.65, },
    { Header: 'Rate', accessor: 'rate', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.66, },
    { Header: 'Qty', accessor: 'qty', resizable: true, sortable: false, visible: true, initialWidthPercentage: 4.30, },
    { Header: 'Amount', accessor: 'amount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.5, },
    { Header: 'USD Amount', accessor: 'localAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.5, },
    { Header: 'Agent', accessor: 'agent', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.62, },
    { Header: 'CUR', accessor: 'agentCur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.65, },
    { Header: 'Agent Amount', accessor: 'agentAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.62, },
    { Header: 'CrDb #', accessor: 'crdbNo', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.62, },
    { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, initialWidthPercentage: 1.32, },
  ],
};

export const otherShipmentBillOrInvoiceSummaryLineItems:TableSettings = {
  tableVersion: 1000,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '200px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',

  columns: [
    { Header: '#', accessor: 'no', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.95, }, 
    { Header: 'Date', accessor: 'date', resizable: true, sortable: false, visible: true, initialWidthPercentage: 5.30, },
    { Header: 'Entity', accessor: 'entity', resizable: true, sortable: false, visible: true, initialWidthPercentage: 13.24, },
    { Header: 'Billing Code', accessor: 'billingCode', resizable: true, sortable: false, visible: true, initialWidthPercentage: 9.93, },
    { Header: 'Item Description', accessor: 'itemDescription', resizable: true, sortable: false, visible: true, initialWidthPercentage: 9.93, },
    { Header: 'Draft', accessor: 'draft', resizable: true, sortable: false, visible: true, initialWidthPercentage: 3.31, },
    { Header: 'CUR', accessor: 'cur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.65, },
    { Header: 'Rate', accessor: 'rate', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.66, },
    { Header: 'Qty', accessor: 'qty', resizable: true, sortable: false, visible: true, initialWidthPercentage: 4.30, },
    { Header: 'Amount', accessor: 'amount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.5, },
    { Header: 'USD Amount', accessor: 'localAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.5, },
    { Header: 'Agent', accessor: 'agent', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.62, },
    { Header: 'CUR', accessor: 'agentCur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.65, },
    { Header: 'Agent Amount', accessor: 'agentAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.62, },
    { Header: 'CrDb #', accessor: 'crdbNo', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.62, },
    { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, initialWidthPercentage: 1.32, },
  ],
};

export const billOrInvoiceSummaryLineItems_noCrDb:TableSettings = {
  tableVersion: 1006,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '200px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',

  columns: [
    { Header: 'Type', accessor: 'type', resizable: true, sortable: false, visible: true, initialWidthPercentage: 3, }, 
    { Header: '#', accessor: 'no', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.95, }, 
    { Header: 'Date', accessor: 'date', resizable: true, sortable: false, visible: true, initialWidthPercentage: 5.30, },
    { Header: 'Entity', accessor: 'entity', resizable: true, sortable: false, visible: true, initialWidthPercentage: 19.48, },
    { Header: 'Billing Code', accessor: 'billingCode', resizable: true, sortable: false, visible: true, initialWidthPercentage: 11.58, },
    { Header: 'Item Description', accessor: 'itemDescription', resizable: true, sortable: false, visible: true, initialWidthPercentage: 21.55, },
    { Header: 'Draft', accessor: 'draft', resizable: true, sortable: false, visible: true, initialWidthPercentage: 3.31, },
    { Header: 'CUR', accessor: 'cur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 2.65, },
    { Header: 'Rate', accessor: 'rate', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.66, },
    { Header: 'Qty', accessor: 'qty', resizable: true, sortable: false, visible: true, initialWidthPercentage: 4.30, },
    { Header: 'Amount', accessor: 'amount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.5, },
    { Header: 'USD Amount', accessor: 'localAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.5, },
    { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, initialWidthPercentage: 1.32, },
  ],
};

export const crdbSummaryLineItems:TableSettings = {
  tableVersion: 1015,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '200px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',

  columns: [
    { Header: 'Type', accessor: 'type', resizable: true, sortable: false, visible: true, initialWidthPercentage: 3, }, 
    { Header: 'CrDb #', accessor: 'crdbNo', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.15, },
    { Header: 'CrDb Date', accessor: 'crdbDate', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.30, },
    { Header: 'Agent', accessor: 'agent', resizable: true, sortable: false, visible: true, initialWidthPercentage: 11.75, },
    { Header: 'Billing Code', accessor: 'billingCode', resizable: true, sortable: false, visible: true, initialWidthPercentage: 13.81, },
    { Header: 'Item Description', accessor: 'itemDescription', resizable: true, sortable: false, visible: true, initialWidthPercentage: 16.81, },
    { Header: 'Draft', accessor: 'draft', resizable: true, sortable: false, visible: true, initialWidthPercentage: 3.94, },
    { Header: 'CUR', accessor: 'cur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 3.15, },
    { Header: 'Credit', accessor: 'credit', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.30, },
    { Header: 'Debit', accessor: 'debit', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.30, },
    { Header: 'CrDb Amount', accessor: 'crdbAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.87, },
    { Header: 'PS', accessor: 'ps', resizable: true, sortable: false, visible: true, initialWidthPercentage: 3.30, },
    { Header: 'B/L #', accessor: 'blNo', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.75, },
    { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, initialWidthPercentage: 1.57, },
  ],
};

export const otherShipmentCrdbSummaryLineItems:TableSettings = {
  tableVersion: 1002,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '200px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',

  columns: [
    { Header: 'CrDb #', accessor: 'crdbNo', resizable: true, sortable: false, visible: true, initialWidthPercentage: 8.15, },
    { Header: 'CrDb Date', accessor: 'crdbDate', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.30, },
    { Header: 'Agent', accessor: 'agent', resizable: true, sortable: false, visible: true, initialWidthPercentage: 14.75, },
    { Header: 'Billing Code', accessor: 'billingCode', resizable: true, sortable: false, visible: true, initialWidthPercentage: 13.81, },
    { Header: 'Item Description', accessor: 'itemDescription', resizable: true, sortable: false, visible: true, initialWidthPercentage: 16.81, },
    { Header: 'Draft', accessor: 'draft', resizable: true, sortable: false, visible: true, initialWidthPercentage: 3.94, },
    { Header: 'CUR', accessor: 'cur', resizable: true, sortable: false, visible: true, initialWidthPercentage: 3.15, },
    { Header: 'Credit', accessor: 'credit', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.30, },
    { Header: 'Debit', accessor: 'debit', resizable: true, sortable: false, visible: true, initialWidthPercentage: 6.30, },
    { Header: 'CrDb Amount', accessor: 'crdbAmount', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.87, },
    { Header: 'PS', accessor: 'ps', resizable: true, sortable: false, visible: true, initialWidthPercentage: 3.30, },
    { Header: 'B/L #', accessor: 'blNo', resizable: true, sortable: false, visible: true, initialWidthPercentage: 7.75, },
    { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, initialWidthPercentage: 1.57, },
  ],
};

export const billOrInvoiceLineItemsWh:TableSettings = {
  tableVersion: 1004,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '320px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',

  columns: [
    {
      Header: '#',
      accessor: 'indicator',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.57,
    },
    {
      Header: 'Type',
      accessor: 'type',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.52,
    },
    {
      Header: 'Billing Code',
      accessor: 'billingCode',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 16.67,
    },
    {
      Header: 'Description',
      accessor: 'itemDescription',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 29.76,
    },
    {
      Header: 'CUR',
      accessor: 'cur',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 5.95,
    },
    {
      Header: 'Rate',
      accessor: 'rate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.71,
    },
    {
      Header: 'Qty',
      accessor: 'qty',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.71,
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.71,
    },
    {
      Header: '',
      accessor: 'scroll',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.40,
    },
  ]
};


export const periodBillOrInvoiceLineItems:TableSettings = {
  tableVersion: 1009,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '320px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',

  columns: [
    {
      Header: '#',
      accessor: 'indicator',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.57,
    },
    {
      Header: 'ID',
      accessor: 'opNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.52,
    },
    {
      Header: 'Billing Code',
      accessor: 'billingCodeView',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 16.65,
    },
    {
      Header: 'Billing Item',
      accessor: 'description',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 29.78,
    },
    {
      Header: 'CUR',
      accessor: 'cur',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 5.95,
    },
    {
      Header: 'Rate',
      accessor: 'rate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.71,
    },
    {
      Header: 'Qty',
      accessor: 'qty',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.71,
    },
    {
      Header: 'Amount',
      accessor: 'amtDesc',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.71,
    },
    {
      Header: '',
      accessor: 'scroll',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.40,
    },
  ]
};

export const periodBillOrInvoiceLineItemsCredit:TableSettings = {
  tableVersion: 1004,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '320px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',

  columns: [
    {
      Header: '#',
      accessor: 'indicator',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.57,
    },
    {
      Header: 'ID',
      accessor: 'opNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.52,
    },
    {
      Header: 'Billing Code',
      accessor: 'billingCodeView',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 16.65,
    },
    {
      Header: 'Billing Item',
      accessor: 'description',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 19.07,
    },
    {
      Header: 'CUR',
      accessor: 'cur',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 5.95,
    },
    {
      Header: 'Rate',
      accessor: 'rate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.71,
    },
    {
      Header: 'Qty',
      accessor: 'qty',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.71,
    },
    {
      Header: 'Amount',
      accessor: 'amtDesc',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.71,
    },
    { 
      Header: 'Agent Amount', 
      accessor: 'agtAmount', 
      resizable: true, 
      sortable: false, 
      visible: true, 
      initialWidthPercentage: 10.71, },
    {
      Header: '',
      accessor: 'scroll',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.40,
    },
  ]
};

export const periodBillOrInvoiceLineItemsWithVat:TableSettings = {
  tableVersion: 1010,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '320px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',

  columns: [
    {
      Header: '#',
      accessor: 'indicator',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.57,
    },
    {
      Header: 'ID',
      accessor: 'opNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.52,
    },
    {
      Header: 'Billing Code',
      accessor: 'billingCodeView',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 15.65,
    },
    {
      Header: 'Billing Item',
      accessor: 'description',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 27.76,
    },
    {
      Header: 'CUR',
      accessor: 'cur',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 5.95,
    },
    {
      Header: 'Rate',
      accessor: 'rate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.71,
    },
    {
      Header: 'Qty',
      accessor: 'qty',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.71,
    },
    {
      Header: 'Amount',
      accessor: 'amtDesc',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.71,
    },
    { 
      Header: 'VAT', 
      accessor: 'useVat', 
      resizable: true, 
      sortable: false, 
      visible: true, 
      initialWidthPercentage: 3.02, },
    {
      Header: '',
      accessor: 'scroll',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.40,
    },
  ]
};

export const periodBillOrInvoiceLineItemsWithVatCredit:TableSettings = {
  tableVersion: 1004,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '320px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',

  columns: [
    {
      Header: '#',
      accessor: 'indicator',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.57,
    },
    {
      Header: 'ID',
      accessor: 'opNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.52,
    },
    {
      Header: 'Billing Code',
      accessor: 'billingCodeView',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 15.65,
    },
    {
      Header: 'Billing Item',
      accessor: 'description',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 17.01,
    },
    {
      Header: 'CUR',
      accessor: 'cur',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 5.95,
    },
    {
      Header: 'Rate',
      accessor: 'rate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.71,
    },
    {
      Header: 'Qty',
      accessor: 'qty',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.71,
    },
    {
      Header: 'Amount',
      accessor: 'amtDesc',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 10.71,
    },
    { 
      Header: 'Credit Amount', 
      accessor: 'agtAmount', 
      resizable: true, 
      sortable: false, 
      visible: true, 
      initialWidthPercentage: 10.71, },
    { 
      Header: 'VAT', 
      accessor: 'useVat', 
      resizable: true, 
      sortable: false, 
      visible: true, 
      initialWidthPercentage: 3.02, },
    {
      Header: '',
      accessor: 'scroll',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.40,
    },
  ]
};

export const periodCrdbLineItems:TableSettings = {
  tableVersion: 1006,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '320px',
  useStorage: true,
  flexResizer: true,
  headersColor: 'white',

  columns: [
    {
      Header: '#',
      accessor: 'indicator',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 3.57,
    },
    {
      Header: 'Billing Type',
      accessor: 'billingType',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 8.33,
    },
    {
      Header: 'ID',
      accessor: 'opNo',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.52,
    },
    {
      Header: 'Billing Code',
      accessor: 'crDbCodeView',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 14.29,
    },
    {
      Header: 'Description',
      accessor: 'description',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 17.86,
    },
    {
      Header: 'CUR',
      accessor: 'cur',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 5.95,
    },
    {
      Header: 'Rate',
      accessor: 'rate',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.52,
    },
    {
      Header: 'Qty',
      accessor: 'qty',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.52,
    },
    {
      Header: 'Debit Amount',
      accessor: 'debit',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.52,
    },
    {
      Header: 'Credit Amount',
      accessor: 'credit',
      resizable: true,
      sortable: false,
      visible: true,
      initialWidthPercentage: 9.52,
    },
    {
      Header: '',
      accessor: 'scroll',
      resizable: false,
      sortable: false,
      visible: true,
      initialWidthPercentage: 2.40,
    },
  ]
};

export const oceanImportIsfList:TableSettings = {
  tableVersion: 10005,
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: false,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'stat', Header: '+', visible: false, width: 30, canBeHidden: false, resizable: false, sortable: false, filterable: false, filterType: 'text' },
    { accessor:'userId', Header: 'User ID', visible: false, width: 80, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'status', Header: 'Status', visible: false, width: 200, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'branch', Header: 'BR', visible: false, width: 40, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'disp', Header: 'Disp', visible: false, width: 60, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'lastAction', Header: 'Last Action', visible: false, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'isfNo', Header: 'ISF #', visible: false, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'shipmentId', Header: 'Shipment ID', visible: false, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'impName', Header: 'Importer Name', visible: false, width: 150, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'impNo', Header: 'Importer #', visible: false, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'hblNo', Header: 'HBL #', visible: false, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'tariffNo', Header: 'HTS Code', visible: false, width: 100, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'isfDate', Header: 'ISF Date', visible: false, width: 75, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'bondHolder', Header: 'Bond Holder', visible: false, width: 250, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
    { accessor:'u2Date', Header: 'Update', visible: false, width: 130, canBeHidden: true, resizable: true, sortable: true, filterable: true, filterType: 'text' },
  ]
};

export const codesUserGroupList:TableSettings = {
  tableVersion: 10043,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'white',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 5.88, },
    { accessor:'name', Header: 'Group Name', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 22.06,},
    { accessor:'desc', Header: 'Description', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 36.76, },
    { accessor:'inactive', Header: 'Inactive', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'bool', initialWidthPercentage: 8.82, },
    { accessor:'inactiveDate', Header: 'Inactive Date', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text', initialWidthPercentage: 11.76, },
    { accessor:'inactiveBy', Header: 'Inactive By', visible: true, resizable: true, sortable: true, filterable: true, filterType: 'text',initialWidthPercentage: 11.76, },
    { accessor: 'scroll', Header: '', resizable: false, sortable: false, visible: true, initialWidthPercentage: 2.96, },
  ]
};

export const pimsMasterDownloadList:TableSettings = {
  tableVersion: 1012,
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: false,

  headersColor: 'gray',
  alternateRows: true,
  columns: []
};

export const pimsHouseDownloadList:TableSettings = {
  tableVersion: 1016,
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: false,

  headersColor: 'gray',
  alternateRows: true,
  columns: []
};

export const pimsShipmentList:TableSettings = {
  tableVersion: 1007,
  overflowX: 'scroll',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: false,

  headersColor: 'gray',
  alternateRows: true,
  columns: []
};

export const lastTransactionTable: TableSettings = {
  tableVersion: 1010,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { Header: '', accessor: 'indicator', resizable: true, sortable: false, visible: true, width: 30 },
    { Header: 'Op Type', accessor: 'type', resizable: true, sortable: false, visible: true, width: 60 },
    { Header: 'MBL #', accessor: 'mblNo', resizable: true, sortable: false, visible: true, width: 100 },
    { Header: 'HBL #', accessor: 'hblNo', resizable: true, sortable: false, visible: true, width: 100 },
    { Header: 'Ref. #', accessor: 'refNo', resizable: true, sortable: false, visible: true, width: 100 },
    { Header: 'ETA', accessor: 'eta', resizable: true, sortable: false, visible: true, width: 80 },
    { Header: 'Final Destination', accessor: 'finalDest', resizable: true, sortable: false, visible: true, width: 112 },
    { Header: 'ETD / ETA', accessor: 'postDate', resizable: true, sortable: false, visible: true, width: 80 },
  ]
};

export const lastTransactionTableWithBT: TableSettings = {
  tableVersion: 10011,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { Header: '', accessor: 'indicator', resizable: true, sortable: false, visible: true, width: 30 },
    { Header: 'OP Type', accessor: 'type', resizable: true, sortable: false, visible: true, width: 60 },
    { Header: 'MBL #', accessor: 'mblNo', resizable: true, sortable: false, visible: true, width: 100 },
    { Header: 'HBL #', accessor: 'hblNo', resizable: true, sortable: false, visible: true, width: 100 },
    { Header: 'PRO #', accessor: 'refNo', resizable: true, sortable: false, visible: true, width: 100 },
    { Header: 'Arrival', accessor: 'eta', resizable: true, sortable: false, visible: true, width: 80 },
    { Header: 'Destination', accessor: 'finalDest', resizable: true, sortable: false, visible: true, width: 112 },
    { Header: 'Date', accessor: 'postDate', resizable: true, sortable: false, visible: true, width: 80 },
  ]
};

export const utTaskMemoListItem:TableSettings = {
  tableVersion: 1016,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { Header: '', accessor: 'indicator', resizable: true, sortable: true, visible: true, width: 30 },
    { Header: 'Task', accessor: 'task', resizable: true, sortable: true, visible: true, width: 50 },
    { Header: 'Asigned', accessor: 'asigned', resizable: true, sortable: true, visible: true, width: 100 },
    { Header: 'Remind Date', accessor: 'remindDate', resizable: true, sortable: true, visible: true, width: 130 },
    { Header: 'Due Date', accessor: 'dueDate', resizable: true, sortable: true, visible: true, width: 130 },
    { Header: 'Type' , accessor: 'opType', resizable: true, sortable: true, visible: true, width: 80},
    { Header: 'Pending', accessor: 'pending', resizable: true, sortable: true, visible: true, width: 50 },
    { Header: 'Reference', accessor: 'referenceNo', resizable: true, sortable: true, visible: true, width: 300 },
    { Header: 'Title', accessor: 'title', resizable: true, sortable: true, visible: true, width: 200 },
    { Header: 'Contents', accessor: 'contents', resizable: true, sortable: true, visible: true, width: 300 },
    { Header: 'Create', accessor: 'createdAt', resizable: true, sortable: true, visible: true, width: 80 },
    { Header: 'Create By', accessor: 'createdBy', resizable: true, sortable: true, visible: true, width: 100 },
  ],
};

export const utAttachedFileListItem:TableSettings = {
  tableVersion: 1004,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { Header: '', accessor: 'indicator', resizable: true, sortable: true, visible: true, width: 30 },
    { Header: 'User ID', accessor: 'userId', resizable: true, sortable: true, visible: true, width: 90 },
    { Header: 'Date', accessor: 'date', resizable: true, sortable: true, visible: true, width: 65 },
    { Header: 'Ref. #', accessor: 'refNo', resizable: true, sortable: true, visible: true, width: 100 },
    { Header: 'Title', accessor: 'title', resizable: true, sortable: true, visible: true, width: 170 },
    { Header: 'Attached File Name', accessor: 'fileName', resizable: true, sortable: true, visible: true, width: 170 },
    { Header: 'File Size', accessor: 'fileSize', resizable: true, sortable: true, visible: true, width: 80 },
    { Header: 'Contents', accessor: 'contents', resizable: true, sortable: true, visible: true, width: 800 },
  ],
};

export const utCommunicationListItem: TableSettings = {
  tableVersion: 1006,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { Header: '', accessor: 'indicator', resizable: true, sortable: true, visible: true, width: 30 },
    { Header: 'Log Type', accessor: 'logType', resizable: true, sortable: true, visible: true, width: 80 },
    { Header: 'Ref #', accessor: 'refNo', resizable: true, sortable: true, visible: true, width: 100 },
    { Header: 'HBL #', accessor: 'hblNo', resizable: true, sortable: true, visible: true, width: 100 },
    { Header: 'User Name', accessor: 'senderId', resizable: true, sortable: true, visible: true, width: 80 },
    { Header: 'Title', accessor: 'title', resizable: true, sortable: true, visible: true, width: 200 },
    { Header: 'Recipient', accessor: 'recipient', resizable: true, sortable: true, visible: true, width: 300 },
    { Header: 'Company Name', accessor: 'recCompanyName', resizable: true, sortable: true, visible: true, width: 140 },
    { Header: 'Type', accessor: 'sType', resizable: true, sortable: true, visible: true, width: 50 },
    { Header: 'Email Address', accessor: 'email', resizable: true, sortable: true, visible: true, width: 140 },
    { Header: 'Status', accessor: 'state', resizable: true, sortable: true, visible: true, width: 80 },
    { Header: 'Date', accessor: 'postDate', resizable: true, sortable: true, visible: true, width: 120 },
    { Header: 'Fax #', accessor: 'faxNo', resizable: true, sortable: true, visible: true, width: 140 },
  ],
};

export const utFaxLogListItem:TableSettings = {
  tableVersion: 1003,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { Header: '', accessor: 'indicator', resizable: true, sortable: true, visible: true, width: 30 },
    { Header: 'Ref #', accessor: 'refNo', resizable: true, sortable: true, visible: true, width: 100 },
    { Header: 'HBL #', accessor: 'hblNo', resizable: true, sortable: true, visible: true, width: 100 },
    { Header: 'Date', accessor: 'postDate', resizable: true, sortable: true, visible: true, width: 120 },
    { Header: 'User Name', accessor: 'senderId', resizable: true, sortable: true, visible: true, width: 100 },
    { Header: 'Company Name', accessor: 'recCompanyName', resizable: true, sortable: true, visible: true, width: 140 },
    { Header: 'Recipient', accessor: 'recipient', resizable: true, sortable: true, visible: true, width: 140 },
    { Header: 'Status', accessor: 'state', resizable: true, sortable: true, visible: true, width: 60 },
    { Header: 'Fax #', accessor: 'recNo', resizable: true, sortable: true, visible: true, width: 120 },
    { Header: 'Title', accessor: 'title', resizable: true, sortable: true, visible: true, width: 400 },
  ],
};

export const utPrintLogListItem:TableSettings = {
  tableVersion: 1003,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { Header: '', accessor: 'indicator', resizable: true, sortable: true, visible: true, width: 30 },
    { Header: 'Ref #', accessor: 'refNo', resizable: true, sortable: true, visible: true, width: 100 },
    { Header: 'HBL #', accessor: 'hblNo', resizable: true, sortable: true, visible: true, width: 100 },
    { Header: 'User Name', accessor: 'senderId', resizable: true, sortable: true, visible: true, width: 80 },
    { Header: 'Title', accessor: 'title', resizable: true, sortable: true, visible: true, width: 500 },
    { Header: 'Date', accessor: 'postDate', resizable: true, sortable: true, visible: true, width: 120 },
  ],
};

export const utEmailLogListItem:TableSettings = {
  tableVersion: 1004,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { Header: '', accessor: 'indicator', resizable: true, sortable: true, visible: true, width: 30 },
    { Header: 'Ref #', accessor: 'refNo', resizable: true, sortable: true, visible: true, width: 100 },
    { Header: 'HBL #', accessor: 'hblNo', resizable: true, sortable: true, visible: true, width: 100 },
    { Header: 'User Name', accessor: 'senderId', resizable: true, sortable: true, visible: true, width: 80 },
    { Header: 'Title', accessor: 'title', resizable: true, sortable: true, visible: true, width: 200 },
    { Header: 'Recipient', accessor: 'recipient', resizable: true, sortable: true, visible: true, width: 300 },
    { Header: 'Company Name', accessor: 'recCompanyName', resizable: true, sortable: true, visible: true, width: 140 },
    { Header: 'Type', accessor: 'sType', resizable: true, sortable: true, visible: true, width: 50 },
    { Header: 'Email Address', accessor: 'recNo', resizable: true, sortable: true, visible: true, width: 140 },
    { Header: 'Status', accessor: 'state', resizable: true, sortable: true, visible: true, width: 80 },
    { Header: 'Date', accessor: 'postDate', resizable: true, sortable: true, visible: true, width: 120 },
  ],
};

export const securityControlTable: TableSettings = {
  tableVersion: 1002,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, width: 35, },
    { Header: 'Control Name', accessor: 'name', resizable: true, sortable: false, visible: true, width: 120 },
    { Header: 'Control Description', accessor: 'desc', resizable: true, sortable: false, visible: true, width: 180 },
    { Header: 'Task Description', accessor: 'taskDesc', resizable: true, sortable: false, visible: true, width: 180 },
    { Header: 'Attribute', accessor: 'attrName', resizable: true, sortable: false, visible: true, width: 90 },
  ]
};

export const securityControlListTable: TableSettings = {
  tableVersion: 1006,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: true, filterType: 'text', width: 35, },
    { Header: 'Description', accessor: 'description', resizable: true, sortable: true, filterType: 'text', visible: true, width: 290 },
    { Header: 'Grid Form', accessor: 'gridForm', resizable: true, sortable: true, filterType: 'number', visible: true, width: 95 },
    { Header: 'Detail Form', accessor: 'detailForm', resizable: true, sortable: true, filterType: 'text', visible: true, width: 120 },
    { Header: 'Menu Name', accessor: 'menuName', resizable: true, sortable: true, filterType: 'text', visible: true, width: 200 },
  ]
};

export const bankBalanceList:TableSettings = {
  tableVersion: 10084,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '250px',
  width: '1170px',
  useStorage: true,
  flexResizer: true,

  columns: [
    { accessor:'indicator', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 3, },
    { accessor:'glname', Header: 'Bank Entity', visible: true, resizable: true, sortable: false, initialWidthPercentage: 10,},
    { accessor:'accountcode', Header: 'Account Code', visible: true, resizable: true, sortable: false, initialWidthPercentage: 8, },
    { accessor:'currency', Header: 'CUR', visible: true, resizable: true, sortable: false, initialWidthPercentage: 4.0, },
    { accessor:'prevbalance', Header: 'Previous Balance', visible: true, resizable: true, sortable: false, initialWidthPercentage: 10, },
    { accessor:'debit', Header: 'In', visible: true, resizable: true, sortable: false, initialWidthPercentage: 8.0, },
    { accessor:'credit', Header: 'Out', visible: true, resizable: true, sortable: false, initialWidthPercentage: 8.0, },
    { accessor:'balance', Header: 'Current Balance', visible: true, resizable: true, sortable: false, initialWidthPercentage: 10.0 },
    { accessor:'odepo', Header: 'Processing Deposit Check', visible: true, resizable: true, sortable: false, initialWidthPercentage: 9.0 },
    { accessor:'ocheck', Header: 'Processing Paid Check', visible: true, resizable: true, sortable: false, initialWidthPercentage: 9, },
    { accessor:'ablebalance', Header: 'Available Balance', visible: true, resizable: true, sortable: false, initialWidthPercentage: 10, },
    { accessor:'recon', Header: 'Recon', visible: true, resizable: true, sortable: false, initialWidthPercentage: 4 },
    { accessor:'detail', Header: 'Detail', visible: true, resizable: true, sortable: false, initialWidthPercentage: 5 },
    { Header: '', accessor: 'scroll', resizable: false, sortable: false, visible: true, initialWidthPercentage: 2, },
  ]
};

export const aiMawbDirectBlLineItems: TableSettings = {
    tableVersion: 10003,
    overflowX: 'hidden',
    overflowY: 'hidden',
    
    useStorage: true,
    flexResizer: true,
  
    headersColor: 'white',
    columns: [
        {
            Header: 'Mark and Numbers',
            accessor: 'markAndNumbers',
            resizable: true,
            sortable: false,
            visible: true,
            initialWidthPercentage: 20.27,
        },
        {
            Header: 'Package(s)',
            accessor: 'numberOfPackages',
            resizable: true,
            sortable: false,
            visible: true,
            initialWidthPercentage: 14.86,
        },
        {
            Header: 'Description of Goods',
            accessor: 'descriptionOfGoods',
            resizable: true,
            sortable: false,
            visible: true,
            initialWidthPercentage: 29.73,
        },
        {
            Header: 'Gross Weight',
            accessor: 'grossWeight',
            resizable: true,
            sortable: false,
            visible: true,
            initialWidthPercentage: 17.57,
        },
        {
            Header: 'Volume Weight',
            accessor: 'volumeWeight',
            resizable: true,
            sortable: false,
            visible: true,
            initialWidthPercentage: 17.57,
        },
    ]
};

export const oiMblDirectBlLineItems: TableSettings = {
    tableVersion: 10003,
    overflowX: 'hidden',
    overflowY: 'hidden',
    
    useStorage: true,
    flexResizer: true,
  
    headersColor: 'white',
    columns: [
        {
            Header: 'Mark and Numbers',
            accessor: 'markAndNumbers',
            resizable: true,
            sortable: false,
            visible: true,
            initialWidthPercentage: 17.57,
        },
        {
            Header: 'Package(s)',
            accessor: 'numberOfPackages',
            resizable: true,
            sortable: false,
            visible: true,
            initialWidthPercentage: 16.22,
        },
        {
            Header: 'Description of Goods',
            accessor: 'descriptionOfGoods',
            resizable: true,
            sortable: false,
            visible: true,
            initialWidthPercentage: 24.59,
        },
        {
            Header: 'Gross Weight',
            accessor: 'grossWeight',
            resizable: true,
            sortable: false,
            visible: true,
            initialWidthPercentage: 17.57,
        },
        {
            Header: 'Measurement',
            accessor: 'measurement',
            resizable: true,
            sortable: false,
            visible: true,
            initialWidthPercentage: 6.48,
        },
    ]
};

export const buyingRateList: TableSettings = {
  tableVersion: 10014,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
      { accessor: 'indicator', Header: ' ', visible: true, resizable: false, sortable: false, initialWidthPercentage: 4.2, },
      { accessor: 'carrier', Header: 'Carrier', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', initialWidthPercentage: 11 },
      { accessor: 'mode', Header: 'Mode', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', initialWidthPercentage: 5.21, },
      { accessor: 'origin', Header: 'Origin', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', initialWidthPercentage: 11, },
      { accessor: 'destination', Header: 'Destination', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', initialWidthPercentage: 11, },
      { accessor: 'commodity', Header: 'Commodity', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', initialWidthPercentage: 13.9, },
      { accessor: 'size', Header: 'CNTR Type', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', initialWidthPercentage: 8, },
      { accessor: 'transit', Header: 'Transit', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'number', initialWidthPercentage: 6.00, formatString: '#,##0' },
      { accessor: 'rate1', Header: 'Rate', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'number', initialWidthPercentage: 5.9, },
      { accessor: 'effectETD', Header: 'Valid From', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'date', initialWidthPercentage: 7.02, },
      { accessor: 'expireETD', Header: 'Valid by', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'date', initialWidthPercentage: 7.02, },
      { accessor: 'remark', Header: 'Remark', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', initialWidthPercentage: 9.55, },
      { accessor: 'scroll', Header: '', visible: true, resizable: true, sortable: false, filterable: true, filterType: 'text', initialWidthPercentage: 3, },
  ]
};

export const internetTrackingList: TableSettings = {
  tableVersion: 1023,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { Header: '', accessor: 'indicator', resizable: true, sortable: false, visible: true, width: 30 },
    { Header: 'Inacitve', accessor: 'inactive', resizable: true, sortable: false, visible: true, width: 50 },
    { Header: 'User Id', accessor: 'userId', resizable: true, sortable: false, visible: true, width: 100 },
    { Header: 'Password', accessor: 'password', resizable: true, sortable: false, visible: true, width: 100 },
    { Header: 'Company Name', accessor: 'companyName', resizable: true, sortable: false, visible: true, width: 250 },
    { Header: 'Agent', accessor: 'isAgent', resizable: true, sortable: false, visible: true, width: 50 },
    { Header: 'Customer', accessor: 'isCustomer', resizable: true, sortable: false, visible: true, width: 50 },
    { Header: 'Shipper', accessor: 'isShipper', resizable: true, sortable: false, visible: true, width: 50 },
    { Header: 'Consignee', accessor: 'isConsignee', resizable: true, sortable: false, visible: true, width: 60 },
  ]
};

export const internetTrackingCompanyList: TableSettings = {
  tableVersion: 1027,
  overflowX: 'hidden',
  overflowY: 'scroll',
  height: '100%',
  useStorage: true,
  flexResizer: true,

  headersColor: 'gray',
  alternateRows: true,
  columns: [
    { Header: '', accessor: 'indicator', resizable: true, sortable: false, visible: true, width: 30 },
    { Header: 'Company', accessor: 'companyName', resizable: true, sortable: false, visible: true, width: 190 },
    { Header: 'Agent', accessor: 'isAgent', resizable: true, sortable: false, visible: true, width: 70 },
    { Header: 'Customer', accessor: 'isCustomer', resizable: true, sortable: false, visible: true, width: 70 },
    { Header: 'Shipper', accessor: 'isShipper', resizable: true, sortable: false, visible: true, width: 70 },
    { Header: 'Consignee', accessor: 'isConsignee', resizable: true, sortable: false, visible: true, width: 70 },
    { Header: 'Phone', accessor: 'phone', resizable: true, sortable: false, visible: true, width: 120 },
    { Header: 'Fax', accessor: 'fax', resizable: true, sortable: false, visible: true, width: 120 },
  ]
};

